/**
 * Shared CI/GitOps types — contract between backend and CLI.
 *
 * Source of truth: workers/backend/src/routes/api/ci/types.ts
 * The backend may have extra optional fields (e.g. connection_id, target_id)
 * that the CLI ignores — this file carries the full (superset) definitions.
 */

import { z } from 'zod';
import {
  validateToolInputSchema,
  containsKanbanPlaceholders,
  extractToolParameters,
} from '../tool-schema-validator';
import {
  kanbanStatusesArraySchema,
  lanesArraySchema,
  validateLaneReferences,
  type FollowupType,
  type KanbanOutcomeInput,
} from './setup/kanban';
import { CONTEXT_BOUNDARIES, toolReplaySettingsSchema } from './setup/tools';
import type { ReplayOverride } from '../catalog/tool-replay';
import { isCustomerFacingAgentRole } from '../constants';
import { endUserDailyMessageLimitField, type SyncWarning } from './setup/hubs';
import {
  AGENT_PERSON_ACCESS_REFUSAL,
  PERSON_ACCESS_KEY,
  heldAccessAssignmentSchema,
  holdsPersonAccess,
  type AccessAssignment,
} from './setup/access-dimensions';
import {
  HUB_PRESENTATION_FIELDS,
  HUB_PRESENTATION_SCHEMAS,
  type HubPresentationColumns,
} from './hub-presentation';
import {
  MONITOR_TRIGGERS,
  MONITOR_IDLE_DELAY_REQUIRED_MESSAGE,
  MONITOR_INPUT_SHAPING_IDLE_MESSAGE,
  MONITOR_INPUT_SHAPING_KEYS,
  MONITOR_INPUT_SHAPING_SCHEMAS,
  MONITOR_RULE_KEYS,
  MONITOR_RULE_SCHEMAS,
  MONITOR_CALL_KEYS,
  MONITOR_CALL_SCHEMAS,
  MONITOR_CALL_KEY_MESSAGE,
  monitorCallKeysOffTrigger,
  collectMonitorRuleIssues,
  monitorConfigNeedsDelay,
  monitorInputShapingKeysOnIdle,
  monitorRulesStructuredOutputRefusal,
  type MonitorConfig,
} from './setup/agents';
import { evalNameError, evalInputRoleError, evalTurnAttachmentsError, evalInitialStateError, evalPersonIssue, collectTurnAttachmentHashes, type EvalAttachmentRef } from './evals';
import { EVAL_ATTACHMENT_HASH_REGEX } from './storage-keys';
import { MAX_RESOURCE_FILE_SIZE } from '../resources';
import { PAGES_PER_HUB_MAX, PAGE_HTML_MAX_BYTES } from './pages';
import {
  CALL_OPENING_CAPTION_MAX_CHARS,
  CALL_OPENING_LANGUAGES,
  MAX_CALL_OPENING_ENCODED_BYTES,
  callOpeningCaption,
  callOpeningHash,
  callOpeningLanguage,
  type CallOpeningLanguage,
} from './call-openings';
import { FLAG_CONDITION_OPERATORS, type FlagCondition } from '../flag-conditions';
import { checkDecisionsModelBinding, readAgentSettingsModel } from '../decisions-model';

/**
 * Bounds on the push-only `eval_attachment_files` byte carrier (gh #3365). `content_base64`
 * is decoded server-side; without an ENCODED-length bound the decode allocates before the
 * 10 MB decoded check runs, so a single oversized carrier — or too many carriers — can
 * exhaust a Worker isolate. base64 is 4 bytes per 3 input bytes, plus a little slack.
 */
export const MAX_10MB_BASE64_ENCODED_BYTES = Math.ceil(MAX_RESOURCE_FILE_SIZE / 3) * 4 + 16;
export const MAX_EVAL_ATTACHMENT_ENCODED_BYTES = MAX_10MB_BASE64_ENCODED_BYTES;
/** Generous cap on distinct carriers per push — far above any real hub, bounds array work. */
export const MAX_EVAL_ATTACHMENT_CARRIERS = 1000;
/**
 * Aggregate cap on the total encoded bytes of all `eval_attachment_files` in one push. The
 * per-carrier cap alone doesn't bound the sum (many carriers still add up), so this bounds
 * the attachment contribution to the request body well below the isolate memory limit.
 */
export const MAX_EVAL_ATTACHMENT_AGGREGATE_ENCODED_BYTES = 16 * 1024 * 1024;

/** Bounds for the hub resource carrier shared by CI push, sync, and diff (gh #3401). */
export const MAX_RESOURCE_ENCODED_BYTES = MAX_10MB_BASE64_ENCODED_BYTES;
export const MAX_HUB_AS_CODE_RESOURCES = 1000;
export const MAX_HUB_AS_CODE_RESOURCE_FILES = 5000;
/**
 * Leaves headroom for the parsed config, one decoded 10MB file, and normal Worker
 * execution state while still accommodating several changed binary files per push.
 */
export const MAX_RESOURCE_AGGREGATE_ENCODED_BYTES = 32 * 1024 * 1024;
/** Raw JSON envelope for CI config routes: resource + eval carriers plus config headroom. */
export const MAX_CI_CONFIG_BODY_BYTES = 64 * 1024 * 1024;

// =============================================
// Kanban status types
// Field names match the existing JSONB schema in the database and frontend
// (KanbanStatusCard.tsx, FollowupCard.tsx). The mixed casing (camelCase booleans,
// snake_case strings) is inherited from the original frontend implementation.
// =============================================

export interface HubAsCodeKanbanFollowup {
  id?: string;
  order: number;
  type: FollowupType;
  threshold: number;
  timeUnit: 'seconds' | 'minutes' | 'hours' | 'days';
  instructions: string;
  excludedWeekDays?: number[];
  excludeHolidays?: boolean;
  excludedTimeStart?: string;
  excludedTimeEnd?: string;
  /** 'agent_generated' (default) feeds `instructions` to the LLM; 'direct' sends `direct_text` verbatim. */
  delivery_mode?: 'agent_generated' | 'direct';
  /** Verbatim user-facing text. Required when `delivery_mode='direct'`. */
  direct_text?: string;
  /** Approved Meta WhatsApp template id; consulted only outside the WA 24h window. */
  template_whatsapp_id?: string | null;
  /** Default `'route_to_team'` when omitted. Only consulted on `direct` mode + windowed channels outside the window. */
  outside_window_policy?: 'route_to_team' | 'skip';
  /** Default true — followup turn includes tool schemas. Set to false to strip them and save tokens. No effect on `direct` mode. */
  include_tools?: boolean;
  /** `inactivity_after_before_event` only: the `id` of the `before_event` followup in
   *  this status whose fire anchors this chain. */
  after_followup_id?: string;
}

export interface HubAsCodeKanbanStatus {
  /** Stable identifier — set once at create, never edited. Lowercase slug, used in
   *  `conversation.kanban_status`, ClickHouse `data.meta.kanban_status`, tool params,
   *  and `allowed_next_statuses` references. */
  slug: string;
  /** Display label — shown in UI and tool descriptions. Freely editable. */
  name: string;
  order?: number;
  color?: string;
  isInitialStatus?: boolean;
  triggersAgentResponse?: boolean;
  allowsAgentUpdate?: boolean;
  isTerminalStatus?: boolean;
  isSchedulingStatus?: boolean;
  eventName?: string;
  /** Minutes after the scheduled event date at which the conversation is flagged
   *  `event_due` for the support team. Scheduling statuses only. */
  event_due_delay_minutes?: number;
  /** Template prose injected into the agent turn on transition. Supports
   *  `{{path.to.field}}` placeholders (dotted, whitespace-tolerant) and the
   *  reserved `{{additional_data}}` placeholder which expands to the full
   *  payload as 2-space-indented JSON. */
  additional_instructions?: string;
  /** JSON Schema (Draft 2020-12) defining the form fields collected at
   *  transition time. The collected payload is interpolated into
   *  `additional_instructions` and emitted to the agent as a
   *  `<system_additional_instructions>` block. Top-level property name
   *  `additional_data` is reserved (collides with the dump placeholder).
   *  Capped at 16 KB when JSON-serialized. */
  additional_context_schema?: Record<string, unknown>;
  followups?: HubAsCodeKanbanFollowup[];
  /** Slugs this status can transition to. `undefined` = unrestricted (any next).
   *  Empty array is rejected by the validator — use `isTerminalStatus: true` for
   *  no outbound transitions. */
  allowed_next_statuses?: string[];
  /** Optional presentational lane this status belongs to (slug of a hub `lanes[]`
   *  entry). Board-grouping only; not a transition constraint, not agent-visible.
   *  The initial status must not set this. */
  lane_slug?: string;
  /** Closing outcomes offered when a conversation enters this status. Only valid
   *  on the terminal status (`isTerminalStatus: true`); rejected elsewhere. When
   *  present, choosing one is required on the terminal transition, and the chosen
   *  slug is stored on the conversation and ingested to ClickHouse `data.meta.outcome`. */
  outcomes?: HubAsCodeKanbanOutcome[];
}

/** A closing outcome on the terminal kanban status. */
export type HubAsCodeKanbanOutcome = KanbanOutcomeInput;

/** A presentational lane — a hub-level grouping of kanban statuses for board
 *  filtering. Lives inside the hub config (NOT a separate hub-as-code entity like
 *  the eval `journeys`, which own their own YAML files). */
export interface HubAsCodeLane {
  slug: string;
  name: string;
  color?: string;
  order?: number;
}

// =============================================
// HubAsCode payload — JSON representation of hub.yaml + agents/*.yaml
// =============================================

export interface HubAsCodePayload {
  version: number;
  hub_id: string;
  hub_environment: string;
  preview_label?: string | null;
  branch_name?: string | null;
  /**
   * For a preview hub, the id of its linked production hub (null until first
   * publish). Lets the CLI mirror production read-only on every preview pull.
   * Always null on a production hub's own payload (it has no parent).
   */
  production_hub_id?: string | null;

  /**
   * The hub's settings — and its localized end-user presentation (`Partial<HubPresentationColumns>`,
   * `hub-presentation.ts`): its name, welcome message and starter questions as its end users see
   * them, keyed by supported locale. A presentation map left out leaves the stored one unchanged;
   * `null` or `{}` clears it. Pull emits each only when set.
   */
  hub: {
    name: string;
    description?: string;
    hub_type?: string;
    ai_mode?: string;
    timezone?: string;
    /** Hub language (en|pt|es) — localizes the channel-contact-access pending notice. */
    language?: string;
    /** Gate for who may approve/block channel contacts: 'admin' (default) | 'team'. */
    access_approval_role?: string;
    /** Override text for the one-time "your access is pending" notice (else localized default). */
    access_request_message?: string | null;
    /** Minutes of sustained LLM-provider failure before escalating to the team (null → code default). */
    ai_failure_escalation_minutes?: number | null;
    /** Override text for the mid-outage "we're delayed" notice (else localized default). */
    ai_delay_message?: string | null;
    /** Override text for the escalation hand-off notice (else localized default). */
    ai_handoff_message?: string | null;
    non_app_permission?: string;
    kanban_statuses?: HubAsCodeKanbanStatus[];
    /** Presentational lane definitions for board grouping (see HubAsCodeLane). A
     *  hub-config field, NOT a separate YAML entity like `journeys`. */
    lanes?: HubAsCodeLane[];
    // flag_conditions relocated to the conversation_evaluator AGENT (see HubAsCodeAgent.flag_conditions).
    // summarization_threshold_tokens relocated to the summarizer AGENT (see HubAsCodeAgent.summarization_threshold_tokens).
    // monitor_config relocated to the monitor AGENT (see HubAsCodeAgent.monitor_config).
    /** Days of inactivity (no user/team messages) before a conversation is force-closed.
     *  Range 1..180, default 7. Mandatory across every hub. */
    auto_close_inactive_days?: number;
    /** Days an ended conversation's ConversationDO is kept alive so it can still receive
     *  post-hoc annotations before the cleanup cron deletes it. Range 1..30, default 7.
     *  Archival is unaffected (still at close). */
    conversation_retention_days?: number;
    /** Days an ended conversation's `conversation_index` row is retained warm (Ended tab +
     *  chat-hub continuous history) before the cleanup cron reaps it. Range 1..730,
     *  default 365. Emitted only when non-default. Re-clamped to the effective storage
     *  retention at reap time so an index row never outlives its archive. */
    ended_index_retention_days?: number;
    /** Days a terminal eval session (its runs, results and eval conversations) is retained
     *  in HubDO before the weekly retention cron retires it. Range 0..3650; `0` disables
     *  retention (keep forever). Emitted only when the hub sets an explicit override —
     *  omission means "inherit `platform_config.default_eval_retention_days`", so pushing
     *  a config without this field never clears an override set elsewhere — an explicit
     *  `null` is what clears it. Retirement destroys transcripts (HubDO-only);
     *  ClickHouse eval scores survive. */
    eval_retention_days?: number | null;
    /** Messages one end user may send the hub on the app channel per 24 hours, 1..10000.
     *  Emitted only when set; omission leaves the stored limit unchanged, and an explicit
     *  `null` removes it. */
    end_user_daily_message_limit?: number | null;
    /** Org tag slug names (not UUIDs). Resolved against OrgDO `org_tag` on push;
     *  unknown names are rejected. Hub tags gate which org credentials this hub
     *  can resolve via the symmetric overlap rule (see SKILL connections doc). */
    tags?: string[];
    /** Eval-session seed connection, bound BY NAME (gh #3097). References a
     *  connection declared in this hub's `connections:` — a REST API (custom tool)
     *  connection whose `base_url` is the Rekor API origin + a `write:seeds` token.
     *  Resolved name→id on push (base_url validated for parity with the setup PATCH);
     *  round-trips as the connection's display name on pull. Absent = leave unchanged;
     *  explicit `null` = clear (omission does NOT unbind, matching `tags`). */
    seed_connection?: string | null;
  } & Partial<HubPresentationColumns>;

  agents?: HubAsCodeAgent[];
  resources?: HubAsCodeResource[];
  states?: HubAsCodeState[];
  teams?: HubAsCodeTeam[];
  evals?: HubAsCodeEval[];
  journeys?: HubAsCodeJourney[];
  /**
   * Byte carrier for eval/journey attachments (gh #3365) — deduped by `hash`,
   * PUSH-ONLY, never stored and never diffed (the turn refs are what persist and
   * diff). Kept off the turn objects on purpose: base64 on a turn would ride every
   * frozen `scenario_snapshot` and poison the JSON-stringify diff engine. Apply
   * uploads any hash whose R2 object is missing (re-hashing to verify the bytes
   * match the declared hash — content-addressing must not be poisonable). On pull,
   * `download_url` carries a signed link per referenced hash instead.
   */
  eval_attachment_files?: HubAsCodeEvalAttachmentFile[];
  /**
   * Byte carrier for the voice agents' call openings (gh #4418), deduped by `hash` — the
   * `eval_attachment_files` split: the agents' `call_openings` persist and diff, this carries
   * only bytes. A push may leave out the bytes of an opening the hub already holds. On pull,
   * `download_url` carries a signed link per opening instead.
   */
  call_opening_files?: HubAsCodeCallOpeningFile[];
  /**
   * The hub's automations (wayai#4931). An ABSENT key changes nothing; `[]` deletes every
   * automation. While the key is absent, the stored automations still count wherever another key
   * is checked against them — a connection they use is never deleted as unreferenced.
   */
  automations?: HubAsCodeAutomation[];
  /**
   * The hub's own contact lists (wayai#4931 D6a): filters over the org contact book's labels and
   * scope tags, which an automation names by `target.list`. An ABSENT key changes nothing; `[]`
   * deletes every list. While the key is absent, the stored lists still count wherever an
   * automation is checked against them.
   */
  contact_lists?: HubAsCodeContactList[];
  /**
   * The hub's pages (wayai-pro/wayai#5333): each its manifest (`pageManifestSchema`) and its HTML.
   * An ABSENT key changes nothing; `[]` deletes every page. A pull answers every page the hub
   * holds, the stored manifest with every default filled.
   */
  pages?: HubAsCodePage[];

  /** Connections serve as reference targets for `connection_id` fields on agents
   *  and custom tools. Non-secret configuration fields (base_url, headers, settings,
   *  etc.) are round-trippable via pull/push. The bound org credential round-trips
   *  by name via `credential`; the raw secret is never exported. */
  connections?: Array<{
    id?: string;
    name: string;
    type: string;
    service?: string;
    /** Pin an org credential by display name (round-trips on pull). */
    credential?: string;
    /** Bearer Token org credential for dual-credential (API Key + access token)
     *  connectors. Push-only: not round-tripped on pull (the connection row stores
     *  a single credential id, so only the primary `credential` is re-emitted). */
    access_token_credential?: string;
    /** Opt into a deliberately unauthenticated connection for optional-auth connectors
     *  (e.g. a no-auth MCP server). Suppresses the "created without authentication"
     *  push warning. Must not be combined with `credential`. */
    no_auth?: boolean;
    base_url?: string;
    headers?: Record<string, string>;
    settings?: Record<string, unknown>;
    account_sid?: string;
    enabled?: boolean;
    authentication_type?: string;
    /** Opt out of copying this connection's credential into production on publish/sync.
     *  Defaults to true. Round-trips on pull (emitted only when false). */
    sync_credentials_to_production?: boolean;
  }>;
}

/**
 * One hub page as code (wayai-pro/wayai#5333): the manifest a CLI reads from
 * `pages/<slug>/page.yaml` and the HTML from `pages/<slug>/index.html`. The platform holds the
 * manifest to `pageManifestSchema` and the HTML to `pageHtmlIssue`, and keys the page by the
 * manifest's `slug`.
 */
export interface HubAsCodePage {
  manifest: Record<string, unknown>;
  html: string;
}

/** The most JSON text, in characters, a pushed page's manifest takes before it is judged at all. */
export const MAX_HUB_AS_CODE_PAGE_MANIFEST_CHARS = 64 * 1024;

/**
 * A push's `pages:`, bounded before anything reads it: at most a hub's page cap, each manifest at
 * most `MAX_HUB_AS_CODE_PAGE_MANIFEST_CHARS` of JSON and each HTML at most `PAGE_HTML_MAX_BYTES`
 * characters (no longer string is under the byte cap). What else a page breaks is the diff's to
 * report, entry by entry (`page-diff.ts`).
 */
const hubAsCodePagesSchema = z.array(z.object({
  manifest: z.record(z.unknown()).superRefine((manifest, ctx) => {
    if (JSON.stringify(manifest).length > MAX_HUB_AS_CODE_PAGE_MANIFEST_CHARS) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `a page manifest is at most ${MAX_HUB_AS_CODE_PAGE_MANIFEST_CHARS} characters of JSON` });
    }
  }),
  html: z.string().max(PAGE_HTML_MAX_BYTES, `a page's HTML is at most ${PAGE_HTML_MAX_BYTES / 1024} KiB`),
}).strict()).max(PAGES_PER_HUB_MAX, `a hub holds at most ${PAGES_PER_HUB_MAX} pages`);

export interface HubAsCodeAgent {
  id?: string;
  name: string;
  role: string;
  connection: string;
  connection_id?: string;
  instructions?: string;
  additional_context_template?: string;
  enabled?: boolean;
  include_message_timestamps?: boolean;
  settings?: Record<string, unknown>;
  response_format?: {
    type: string;
    schema_name: string;
    schema_json: Record<string, unknown>;
  };
  tools?: {
    native?: Array<string | HubAsCodeNativeTool>;
    delegation?: Array<{
      id?: string;
      type: string;
      tool: string;
      target: string;
      target_id?: string;
      /** Only for `type: 'hub'` (`delegate_to_hub`, H1.5) — the admin's
       *  data-boundary choice (`instruction` | `summary` | `transcript`). */
      context_boundary?: string;
      /** Only for `tool: start_consult_thread` (H1.7b) — the EXPLICIT
       *  consultant→consultant chain opt-in. Chains are off by default. */
      allow_consultant_chain?: boolean;
      /** As {@link HubAsCodeNativeTool}'s `enabled`. */
      enabled?: boolean;
    }>;
    custom?: HubAsCodeCustomTool[];
    /**
     * MCP tools discovered from an MCP Server connection, assigned to this agent.
     * Identity key is `(connection, name)` (an MCP tool name is unique per server).
     *
     * **Omitted vs present (mirrors `evaluation_variables`):** when the `mcp` key
     * is OMITTED, existing MCP tool rows on the agent are left untouched (so tools
     * assigned in the Platform UI survive a push that doesn't manage them). When
     * PRESENT (even as `[]`), the list is AUTHORITATIVE — listed-but-missing tools
     * are created, unlisted MCP tools are removed, matched ones kept. This differs
     * from `native`/`custom` (whose absence deletes them) because MCP tools are
     * dual-origin (UI + GitOps).
     *
     * Push discovers the connection's tools on demand (equivalent to the UI's
     * "Sync MCP tools" action), so a single push can create the connection AND
     * assign its tools — the MCP server must be reachable.
     */
    mcp?: HubAsCodeMcpTool[];
  };
  resources?: HubAsCodeAgentResource[];
  /**
   * Evaluation variables (a.k.a. agent parameters) — the structured fields an
   * evaluator agent writes per conversation/message. Round-trippable via
   * pull/push. Identity key is `name` (unique per agent). When the key is
   * OMITTED from YAML, existing variables are left untouched; when PRESENT
   * (even as `[]`), the list is treated as authoritative and replaces the
   * agent's current variables. Primarily used by `conversation_evaluator` /
   * `message_evaluator` agents.
   */
  evaluation_variables?: HubAsCodeEvaluationVariable[];
  /**
   * Conversation-flagging conditions evaluated post-close (OR semantics). A true
   * agent parameter on the `conversation_evaluator` agent (relocated from the hub
   * section). Round-trippable via pull/push. Meaningful only on the
   * `conversation_evaluator` agent; ignored elsewhere.
   */
  flag_conditions?: FlagCondition[];
  /**
   * Token threshold that fires the rolling summarizer (effective tokens =
   * input + cache_read + cache_creation; default 120000). A true agent parameter
   * on the `summarizer` agent (relocated from the hub section). Round-trippable via
   * pull/push. Meaningful only on the `summarizer` agent; ignored elsewhere.
   */
  summarization_threshold_tokens?: number;
  /**
   * How many of the user's most recent ended conversations this agent receives as
   * context, injected into the conversation's first user message and frozen there for
   * the life of that conversation. `0` = off; bounded by `PREVIOUS_CONVERSATIONS_MAX`.
   * Ignored on background roles (monitor, evaluators, summarizer), which never address
   * the end user.
   *
   * Round-trippable via pull/push with the same patch semantics as every other optional
   * agent key: absent on CREATE is off, absent on UPDATE leaves the stored value alone,
   * and `0` is what clears one.
   */
  previous_conversations_count?: number;
  /**
   * Monitor scheduling + flagging config. A true agent parameter on the `monitor`
   * agent (relocated from the hub section). Round-trippable via pull/push.
   * Meaningful only on the `monitor` agent; ignored elsewhere.
   *
   * `trigger` says WHEN the monitor runs and defaults to `idle` when absent.
   * `delay_seconds` describes the `idle` fire alone, so it is required exactly
   * when the effective trigger is `idle`, and floored at
   * `MONITOR_DELAY_SECONDS_MIN` — both enforced by
   * `refineHubAsCodeMonitorConfig` and the apply/diff normalization, because
   * agents carry no Zod object schema on this path.
   */
  monitor_config?: MonitorConfig | null;
  /**
   * The voice agent's own call openings, by language (`agent.call_openings`, gh #4418):
   * meaningful only on a `pilot_voice` agent. A language left out plays the platform's
   * opening. Round-trippable via pull/push. **Omitted vs present (mirrors
   * `evaluation_variables`):** OMITTED leaves the stored openings alone, so openings set in
   * the web editor survive a push that doesn't manage them; PRESENT (even `{}` or `null`) is
   * authoritative — every language it lists is set, and every other one is cleared.
   */
  call_openings?: Partial<Record<CallOpeningLanguage, HubAsCodeCallOpening>> | null;
  /**
   * The agent's access values (`agent.access_values`, wayai#5358): per dimension of the org, the
   * value ids it may read and write, checked against the org's dimensions on push, which narrow its turns
   * on the record types that enforce a dimension. **Omitted vs present (mirrors `call_openings`):** OMITTED leaves the stored
   * values alone; `null` clears them, so the agent narrows nothing; an object replaces them whole.
   * Changing them takes `hub:admin` on the hub, and an agent holding them that can run code must use
   * its own sandbox (`sandbox_scope: agent`).
   */
  access_values?: AccessAssignment | null;
}

/**
 * One language's call opening on a voice agent (`HubAsCodeAgent.call_openings`). ON DISK the
 * CLI writes `file`, a hub-relative path to the audio; ON THE WIRE it sends `hash`, the
 * sha256 of the bytes, which ride `HubAsCodePayload.call_opening_files` when the hub may not
 * hold them yet. `caption` is what the call bar shows while it plays.
 */
export interface HubAsCodeCallOpening {
  hash?: string;
  file?: string;
  caption: string;
}

/**
 * The bytes of one call opening, at payload level (`HubAsCodePayload.call_opening_files`),
 * keyed by the `hash` the agents' openings name: `content_base64` on push, `download_url` on
 * pull. Push-only bytes are never stored as such: apply checks them (`storeCallOpeningAudio`)
 * and stores them at their content address.
 */
export interface HubAsCodeCallOpeningFile {
  hash: string;
  content_base64?: string;   // push only
  download_url?: string;     // pull only (signed R2 URL)
  content_type?: string;     // pull only: `audio/mpeg` or `audio/mp4`, which names the local file
}

/** Most opening carriers one push takes: three languages for every voice agent a hub holds. */
export const MAX_CALL_OPENING_CARRIERS = 30;
/** Aggregate cap on the encoded opening bytes of one push. */
export const MAX_CALL_OPENING_AGGREGATE_ENCODED_BYTES = 8 * 1024 * 1024;

// Canonical key lists for HubAsCodeAgent and its `tools` block, co-located with
// the interface so the two are edited together. The CI diff engine derives its
// unknown-key warnings (gh #2505) from these — without a single source of truth,
// a hand-maintained copy silently drifts and emits false "unknown key" warnings
// on valid configs once a new field lands. The config validator is `.passthrough()`
// (no runtime schema to reflect), so parity is enforced at COMPILE time below:
//   - `satisfies readonly (keyof …)[]` rejects typo'd/extra entries
//   - `_Expect<_Exhaustive<…>>` rejects a key present on the interface but missing here
// Add a field to the interface → tsc fails until it's added to the matching list.
type _Expect<T extends true> = T;
type _Exhaustive<Keys extends PropertyKey, Listed extends PropertyKey> =
  Exclude<Keys, Listed> extends never ? true : false;

export const HUB_AS_CODE_AGENT_KEYS = [
  'id', 'name', 'role', 'connection', 'connection_id', 'instructions',
  'additional_context_template', 'enabled', 'include_message_timestamps',
  'settings', 'response_format', 'tools', 'resources', 'evaluation_variables',
  'flag_conditions', 'summarization_threshold_tokens', 'previous_conversations_count',
  'monitor_config', 'call_openings', 'access_values',
] as const satisfies readonly (keyof HubAsCodeAgent)[];
type _AgentKeysExhaustive = _Expect<_Exhaustive<keyof HubAsCodeAgent, (typeof HUB_AS_CODE_AGENT_KEYS)[number]>>;

export const HUB_AS_CODE_AGENT_TOOL_KEYS = [
  'native', 'delegation', 'custom', 'mcp',
] as const satisfies readonly (keyof NonNullable<HubAsCodeAgent['tools']>)[];
type _AgentToolKeysExhaustive = _Expect<_Exhaustive<keyof NonNullable<HubAsCodeAgent['tools']>, (typeof HUB_AS_CODE_AGENT_TOOL_KEYS)[number]>>;

/**
 * One evaluation variable on an agent. Maps to a HubDO `agent_parameter` row.
 * Defaults on read (push): `type` → 'string', `required` → true,
 * `is_summary` → false, everything else unset/empty. The emit side (pull)
 * omits any field equal to its default so round-trips stay clean.
 */
export interface HubAsCodeEvaluationVariable {
  /** Variable name the agent writes — unique within the agent. Required. */
  name: string;
  /** 'string' (default) | 'number' | 'boolean' | 'integer' | 'enum'. */
  type?: string;
  description?: string;
  /** Human-facing display label (Analytics columns, UI). */
  label?: string;
  /** Allowed values when `type: enum`. Ignored for other types. */
  enum?: string[];
  /** Defaults to true when omitted (mirrors the UI's new-variable default). */
  required?: boolean;
  /**
   * Analytics grouping. Conventional values: quality | satisfaction |
   * compliance | categorization. Free-form to preserve system-internal
   * markers (e.g. `eval` on message-evaluator defaults) across round-trips.
   */
  category?: string | null;
  /**
   * Marks the rolling conversation-summary field. Only honored for
   * `type: string`; coerced to false otherwise.
   */
  is_summary?: boolean;
}

/**
 * Native tool entry. The string form (e.g. `"update_kanban_status"`) keeps the
 * platform/catalog defaults. The object form lets users override per-instance
 * fields — only `keep_in_history`, `expose_schema_to_llm` and `enabled` are
 * user-managed; other native-tool fields (schema, instructions, operation) come
 * from the catalog and are not round-tripped. Override fields are diff-applied
 * only when explicitly present in YAML — omitted means "leave as is".
 */
export interface HubAsCodeNativeTool {
  id?: string;
  name: string;
  keep_in_history?: boolean;
  expose_schema_to_llm?: boolean;
  /**
   * Whether the tool runs. A pull writes it only as `false`. Omitted leaves the row as it is, unlike a
   * custom tool's: a file pulled before a pull wrote this key holds none on a row that is off.
   */
  enabled?: boolean;
}

/**
 * An MCP tool assigned to an agent (under `tools.mcp`). The tool's schema,
 * description, and config come from the connection's discovered `mcp_tool`
 * catalog — only the reference and `enabled` round-trip through YAML. `connection` (display
 * name) disambiguates which MCP server the tool comes from; `connection_id` and
 * `id` (the tool-row id) are emitted on pull for stable matching.
 */
export interface HubAsCodeMcpTool {
  id?: string;
  name: string;
  connection?: string;
  connection_id?: string;
  /** As {@link HubAsCodeNativeTool}'s `enabled`. */
  enabled?: boolean;
}

export interface HubAsCodeCustomTool {
  id?: string;
  name: string;
  description?: string;
  method?: string;
  path?: string;
  body_format?: string;
  connection?: string;
  connection_id?: string;
  headers?: Record<string, unknown>;
  query_params?: Record<string, unknown>;
  body_params?: Record<string, unknown>;
  config?: Record<string, unknown>;           // OpenAI function schema (tool_config)
  keep_in_history?: boolean;                   // default false; controls whether tool call+result remain in LLM history
  instructions?: string;
  composed_tools?: Array<{
    tool_name: string;
    mappings: Record<string, string | { source: string; type?: string; description?: string }>;
    appended_description?: string;
    success_path?: string;
  }> | null;
  enabled?: boolean;
  expose_schema_to_llm?: boolean;
  /** The hub's replay settings on the tool (wayai#5083; `withToolReplaySettings`). Absent: unset. */
  replay_override?: ReplayOverride;
  idempotency_header?: string;
  irreversible?: boolean;
}

/**
 * Public wire shape for state definitions in hub.yaml. `slug` is optional
 * because the diff pipeline derives it from `name`; normalized internal state
 * values use a separate required-slug type.
 */
export const hubAsCodeStateSchema = z.object({
  id: z.string().min(1).optional(),
  slug: z.string().optional(),
  name: z.string().min(1),
  scope: z.enum(['conversation', 'user']),
  description: z.string().optional(),
  enabled: z.boolean().optional(),
  inject_in_last_message: z.boolean().optional(),
  json_schema: z.record(z.string(), z.unknown()).optional(),
  initial_value: z.record(z.string(), z.unknown()).nullable().optional(),
}).passthrough();

export type HubAsCodeState = z.infer<typeof hubAsCodeStateSchema>;

export interface HubAsCodeTeam {
  id?: string;           // hub_team_id (stable, set by pull)
  name: string;          // team_name
  access_rule?: 'anytime' | 'anytime_if_delegated_once' | 'only_while_delegated';
  initial_team?: boolean;
  display_only_if_claimed?: boolean;  // conversation_display_only_if_claimed
}

export const hubAsCodeResourceFileSchema = z.object({
  id: z.string().min(1).optional(),                // resource_file_id (stable, set by pull)
  path: z.string().min(1),                         // relative path within resource
  title: z.string().optional(),                    // display name (defaults to filename)
  content: z.string().optional(),                  // text content (push + pull)
  content_base64: z.string().optional(),           // binary content (push only)
  download_url: z.string().optional(),             // signed R2 URL (pull only)
  mime_type: z.string().optional(),
  file_size: z.number().int().nonnegative().optional(),
  hash: z.string().regex(EVAL_ATTACHMENT_HASH_REGEX, 'skill file hash must be a lowercase SHA-256 hex digest').optional(),
}).passthrough();

export type HubAsCodeResourceFile = z.infer<typeof hubAsCodeResourceFileSchema>;

export const hubAsCodeResourceSchema = z.object({
  id: z.string().min(1).optional(),
  name: z.string().min(1),
  // No `type`: every resource is a skill (wayai-pro/wayai#5142 step 5). The schema is
  // `.passthrough()`, so a stray `type` rides through and nothing reads it.
  description: z.string().optional(),
  enabled: z.boolean().optional(),
  // No `user_browsable`: the field is retired (wayai#4710). This schema is `.passthrough()`,
  // so YAML pulled before that — and a CLI old enough to still send it — keeps parsing;
  // the diff and apply engines ignore the key.
  skill_name: z.string().optional(),               // skill-only; auto-derived when omitted
  files: z.array(hubAsCodeResourceFileSchema).optional(),
}).passthrough();

export type HubAsCodeResource = z.infer<typeof hubAsCodeResourceSchema>;

export interface HubAsCodeAgentResource {
  name: string;                        // resource display name
  resource_id?: string;                // stable ID
  priority?: number;
  enabled?: boolean;
  // `include_structure_in_prompt` was removed here in PR 5 — structure is now
  // asked for per call with `{{files('tree')}}`. `hubAsCodeConfigSchema` is
  // `.passthrough()`, so YAML pulled before that still parses; the diff engine
  // simply ignores the key.
  use_native_integration?: boolean;    // skill-only: tool-based (false) vs provider-native (true)
  // `write_enabled` was retired with knowledge (wayai-pro/wayai#5142 step 5 C1); YAML pulled before
  // that still parses (passthrough), and the diff engine ignores the key.
}

// =============================================================================
// ORG-AS-CODE — organization-scoped resources synced via `wayai org push/pull`
// (CLI `org/` directory ⇄ `/api/ci/org-skills/*`). Org resources are the
// org-level analog of hub resources: managed in OrgDO, shadowed into linked
// hubs. They carry no agent links (hubs link the shadow), but DO carry
// `environment`/`tags` (which gate hub eligibility) and explicit folders (org
// folders are a real table with metadata, unlike path-implicit hub folders).
// =============================================================================

export interface OrgAsCodeResourceFolder {
  id?: string;                // org_resource_folder_id (stable, set by pull)
  path: string;               // folder path relative to resource root, e.g. "docs/guides"
  description?: string;       // folder_description
}

export interface OrgAsCodeResource {
  id?: string;                // org_resource_id (stable, set by pull)
  name: string;
  description?: string;
  enabled?: boolean;
  // No `user_browsable` (retired, wayai#4710): the org config schema passes it through
  // like the hub one, and the org diff and apply ignore it.
  skill_name?: string;        // skill-only: slugified unique name (auto-derived from name if omitted)
  environment?: 'all' | 'preview' | 'production';  // which hub environments may link this resource
  tags?: string[];            // org_tag UUIDs gating hub eligibility
  folders?: OrgAsCodeResourceFolder[];             // explicit folders (preserves empty/described folders)
  files?: HubAsCodeResourceFile[];                 // identical shape to hub resource files
}

export interface OrgResourcesPayload {
  version: 1;
  organization_id?: string;
  resources?: OrgAsCodeResource[];
}

/**
 * One automation in `hub.yaml` — the API's nested shape (`automations.ts`) with name references.
 * Each hub reference takes a name (`connection`, `template`, `agent`), an id (`*_id`), or both;
 * the id wins when the hub holds it, else the name beside it does (wayai#5531, wayai#5552: a
 * folder copied from another hub carries that hub's ids). A list is named only: a hub list (`target.list`, this hub's `contact_lists:`) or an
 * org list (`target.org_list`, org data with no hub id).
 * `enabled` is config: HubDO derives the automation's arming from it on every write, a push's
 * included (wayai#4931 D15), so a pushed `enabled: true` schedules real fires.
 * A webhook trigger's secret is never config: a push creates none, and a pulled entry carries none
 * (wayai#4931 D13) — the hub's admin rotates one.
 */
export interface HubAsCodeAutomation {
  id?: string;
  name: string;
  description?: string;
  purpose?: string;          // marketing | operational; absent is marketing (`AUTOMATION_PURPOSES`)
  enabled?: boolean;
  trigger: {
    type: string;
    cron?: string;
    timezone?: string;
    event?: string;          // an `event` trigger's event (`AUTOMATION_EVENT_TYPES`)
    source_hub?: string;     // the production hub (id) whose events it listens to; absent: this hub's
    auth?: string;           // webhook: hmac | static_header (`AUTOMATION_WEBHOOK_AUTHS`)
    header?: string;         // webhook, static_header: the header that carries the secret
  };
  target: {
    type: string;
    list?: string;           // hub contact list name (this hub's `contact_lists:`)
    org_list?: string;       // org contact list name (the org contact book's, never the hub's)
  };
  action: {
    type: string;
    channel?: string;
    connection?: string;     // connection display name
    connection_id?: string;
    template?: string;       // WhatsApp template name, on the action's connection
    template_id?: string;
    text?: string;
    agent?: string;          // agent name
    agent_id?: string;
    instructions?: string;
  };
  /**
   * The gate (wayai#4931 D11), by agent name: an `automation_fire` monitor (a fire gate) or, on a
   * `contact_list` target, an `automation_item` monitor (an item gate). Absent or null: none.
   */
  gate?: {
    monitor: string;
  } | null;
}

/**
 * Every key a `hub.yaml` automation entry (and each of its sections) may carry. A push refuses any
 * other key rather than dropping it; the checks below make a field added to `HubAsCodeAutomation`
 * and missing here a compile error, so a pulled entry is never refused for a key pull writes.
 */
export const HUB_AS_CODE_AUTOMATION_KEYS = [
  'id', 'name', 'description', 'purpose', 'enabled', 'trigger', 'target', 'action', 'gate',
] as const satisfies readonly (keyof HubAsCodeAutomation)[];
type _AutomationKeysExhaustive = _Expect<_Exhaustive<keyof HubAsCodeAutomation, (typeof HUB_AS_CODE_AUTOMATION_KEYS)[number]>>;

export const HUB_AS_CODE_AUTOMATION_TRIGGER_KEYS = [
  'type', 'cron', 'timezone', 'event', 'source_hub', 'auth', 'header',
] as const satisfies readonly (keyof HubAsCodeAutomation['trigger'])[];
type _AutomationTriggerKeysExhaustive = _Expect<_Exhaustive<keyof HubAsCodeAutomation['trigger'], (typeof HUB_AS_CODE_AUTOMATION_TRIGGER_KEYS)[number]>>;

export const HUB_AS_CODE_AUTOMATION_TARGET_KEYS = [
  'type', 'list', 'org_list',
] as const satisfies readonly (keyof HubAsCodeAutomation['target'])[];
type _AutomationTargetKeysExhaustive = _Expect<_Exhaustive<keyof HubAsCodeAutomation['target'], (typeof HUB_AS_CODE_AUTOMATION_TARGET_KEYS)[number]>>;

export const HUB_AS_CODE_AUTOMATION_ACTION_KEYS = [
  'type', 'channel', 'connection', 'connection_id', 'template', 'template_id', 'text',
  'agent', 'agent_id', 'instructions',
] as const satisfies readonly (keyof HubAsCodeAutomation['action'])[];
type _AutomationActionKeysExhaustive = _Expect<_Exhaustive<keyof HubAsCodeAutomation['action'], (typeof HUB_AS_CODE_AUTOMATION_ACTION_KEYS)[number]>>;

export const HUB_AS_CODE_AUTOMATION_GATE_KEYS = [
  'monitor',
] as const satisfies readonly (keyof NonNullable<HubAsCodeAutomation['gate']>)[];
type _AutomationGateKeysExhaustive = _Expect<_Exhaustive<keyof NonNullable<HubAsCodeAutomation['gate']>, (typeof HUB_AS_CODE_AUTOMATION_GATE_KEYS)[number]>>;

/**
 * One hub contact list in `hub.yaml` (wayai#4931 D6a): a filter over the org contact book's
 * labels and scope tags, the scope tags named by their org tag names (as `hub.tags` are). No pins.
 */
export interface HubAsCodeContactList {
  id?: string;
  name: string;
  description?: string;
  filter: {
    labels?: string[];
    scope_tags?: string[];   // org tag names
  };
}

/**
 * Every key a `hub.yaml` contact-list entry (and its filter) may carry: a push refuses any other
 * key rather than dropping it. The checks make a field added to `HubAsCodeContactList` and missing
 * here a compile error.
 */
export const HUB_AS_CODE_CONTACT_LIST_KEYS = [
  'id', 'name', 'description', 'filter',
] as const satisfies readonly (keyof HubAsCodeContactList)[];
type _ContactListKeysExhaustive = _Expect<_Exhaustive<keyof HubAsCodeContactList, (typeof HUB_AS_CODE_CONTACT_LIST_KEYS)[number]>>;

export const HUB_AS_CODE_CONTACT_LIST_FILTER_KEYS = [
  'labels', 'scope_tags',
] as const satisfies readonly (keyof HubAsCodeContactList['filter'])[];
type _ContactListFilterKeysExhaustive = _Expect<_Exhaustive<keyof HubAsCodeContactList['filter'], (typeof HUB_AS_CODE_CONTACT_LIST_FILTER_KEYS)[number]>>;

export interface HubAsCodeEval {
  id?: string;
  name: string;
  path?: string | null;
  agent: string;                   // agent display name
  agent_id?: string;               // stable agent UUID
  runs?: number;                   // default 1
  enabled?: boolean;               // default true
  history?: Array<{ role: string; content?: string | null; tool_calls?: unknown; tool_call_id?: string; attachments?: EvalAttachmentRef[] }>;
  input: { role: string; content?: string | null; tool_calls?: unknown; attachments?: EvalAttachmentRef[] };
  expected: { role: string; content?: string | null; tool_calls?: unknown };
  evaluator_instructions?: string;
  /**
   * Per-run variables (gh #3007): a map of author-provided arrays. Run i of
   * `runs` resolves `{{var(NAME)}}` / `{{var(NAME).field}}` in input/history/
   * expected/evaluator_instructions against `variables.NAME[i-1]`, so a
   * data-mutating eval leases a disjoint fixture per run. Each array MUST cover
   * `runs` (validated at run-launch). Values may be objects (one index → a
   * coherent bundle).
   */
  variables?: Record<string, unknown[]>;
  /**
   * Eval-session seed fixture (gh #3090): the named Rekor fixture this scenario's
   * set depends on. At run-start `run-eval` resets it before the session and
   * clears it after (try/finally). All enabled scenarios in a set MUST agree on
   * one fixture (2+ distinct → the run is rejected). Absent → no seeding (today's
   * behavior). Pairs with `variables` for parallel-safe MUTATING evals.
   */
  fixture?: string;
  /**
   * The Rekor base the fixture is seeded against (gh #3090). REQUIRED whenever
   * `fixture` is declared: the hub seed connection supplies the API origin +
   * token, this supplies the base. A `fixture` with no `target_base` resolves to
   * `incomplete_seed_config` (`resolveEvalFixtureTarget`) and aborts the session
   * with `fixture_seed_failed` (no runs scored) — there is no derived-base
   * fallback. Must agree across a set, same as `fixture`.
   */
  target_base?: string;
  /**
   * User-scope state to seed before this scenario's trigger runs (gh #3430). Each
   * entry `{ slug, scope: 'user', value }` writes `value` to an EXISTING user-scope
   * state definition (`slug`) for the eval's synthetic per-run user, so behavior that
   * depends on memory of prior conversations (a recurring-user shortcut, a "confirmed"
   * flag) is testable. Isolated + torn down per session, like `fixture`. A slug the
   * hub doesn't define, or a value its `json_schema` rejects, degrades the run with a
   * warning — it never poisons shared state.
   */
  initial_state?: Array<{ slug: string; scope?: 'user'; value: Record<string, unknown> }>;
  /**
   * The persons this scenario's runs are for (wayai-pro/wayai#5473 2-eval): person ids or contact ids, named by
   * every Base Toolset call of a run as its customer. Honoured on a preview hub only; a production hub's eval
   * names none. See `evalPersonSchema`. Its retired alias `customer_contact_id` is refused on push, diff and sync
   * (`evalPersonIssue`, #5473 3c).
   */
  person?: string[] | null;
}

/**
 * The bytes for one eval/journey attachment, carried at payload level (see
 * `HubAsCodePayload.eval_attachment_files`). `content_base64` on push, `download_url`
 * on pull — the same split `HubAsCodeResourceFile` uses. Keyed by `hash`, the content
 * address the turn refs point at.
 */
export interface HubAsCodeEvalAttachmentFile {
  hash: string;
  content_base64?: string;   // push only
  download_url?: string;     // pull only (signed R2 URL)
  file_name?: string;
  mime_type?: string;
  file_size?: number;
}

/**
 * One turn of a journey's happy-path transcript — the replayed message-history
 * shape plus an optional stable `step_id` (server-minted on each step's
 * concluding assistant turn; carried so per-step `step_config` overrides and the
 * derived evals stay keyed across a pull → push round-trip).
 */
export interface HubAsCodeJourneyTurn {
  role: string;
  content?: string | null;
  tool_calls?: unknown;
  tool_call_id?: string;
  step_id?: string;
  /** User-turn-only file references — see `EvalAttachmentRef` (gh #3365). */
  attachments?: EvalAttachmentRef[];
}

/** Per-step override config, keyed by `step_id` (mirrors the eval `runs` field). */
export interface HubAsCodeJourneyStepOverride {
  runs?: number;
  evaluator_instructions?: string | null;
}

/**
 * An eval JOURNEY — a stored happy-path transcript that materializes into
 * single-turn evals. First-class hub-as-code entity (one YAML file per journey;
 * the journey owns its managed scenario_set, which is NOT authored). The derived
 * evals stay server-managed and are excluded from the eval diff.
 */
export interface HubAsCodeJourney {
  id?: string;                       // journey_id (stable, set by pull; primary match key)
  name: string;                      // journey_name
  agent: string;                     // responder_agent_id, resolved by display name
  agent_id?: string;                 // stable agent UUID
  enabled?: boolean;                 // default true
  evaluator_instructions?: string;
  transcript?: HubAsCodeJourneyTurn[];
  /** Per-step overrides keyed by `step_id`. */
  step_config?: Record<string, HubAsCodeJourneyStepOverride>;
  /**
   * Per-run variables (gh #3007): a map of author-provided arrays, propagated to
   * every derived step eval. Run i of the whole journey resolves `{{var(NAME)}}`
   * against `variables.NAME[i-1]`, so the journey run leases one coherent fixture.
   * Each array MUST cover the step's `runs` (validated at run-launch).
   */
  variables?: Record<string, unknown[]>;
  /**
   * The persons this journey's steps are for (#5473 2-eval; a journey's by owner decision 141): propagated to
   * every derived step eval as `variables` is, so each step's runs name them as a scenario's do
   * (`HubAsCodeEval.person`). Honoured on a preview hub only; its retired alias is refused as a scenario's is.
   */
  person?: string[] | null;
  /**
   * Eval-session seed fixture (gh #3090): the named Rekor fixture this journey
   * depends on. A journey owns its managed set, so this is the set-level fixture —
   * reset before / cleared after the journey run (try/finally). Absent → no
   * seeding. Pairs with `variables` for parallel-safe MUTATING journeys.
   */
  fixture?: string;
  /**
   * The Rekor base the fixture is seeded against — REQUIRED alongside `fixture`
   * (absence aborts the run with `fixture_seed_failed`). See
   * `HubAsCodeEval.target_base`.
   */
  target_base?: string;
}

// =============================================
// Zod request schemas
// =============================================

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ciUuidSchema = z.string().regex(uuidPattern, 'must be a valid UUID');

/**
 * Walk `agents[].tools.custom[].config.parameters` and add draft-2020-12
 * violations as Zod issues (gh #1542). Placeholder-token cases are skipped
 * — see the matching logic in contracts/setup/tools.ts.
 */
function refineHubAsCodeCustomTools(
  config: unknown,
  ctx: z.RefinementCtx,
): void {
  const agents = (config as { agents?: unknown }).agents;
  if (!Array.isArray(agents)) return;

  for (let a = 0; a < agents.length; a++) {
    const agent = agents[a] as { tools?: { custom?: unknown } } | null | undefined;
    const custom = agent?.tools?.custom;
    if (!Array.isArray(custom)) continue;

    for (let c = 0; c < custom.length; c++) {
      const tool = custom[c] as { name?: string; config?: unknown } | null | undefined;
      // The replay settings, by the REST bodies' own schema: each on its own, whatever the others hold.
      const replay = toolReplaySettingsSchema.safeParse(tool ?? {});
      for (const issue of replay.success ? [] : replay.error.issues) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['agents', a, 'tools', 'custom', c, ...issue.path],
          message: `custom tool "${tool?.name ?? '<unnamed>'}": ${issue.path.join('.')}: ${issue.message}`,
        });
      }
      const raw = tool?.config;
      if (raw == null) continue;

      const params = extractToolParameters(raw);
      if (params === undefined) {
        // Either `config` is not an object shape, or there's no `parameters`
        // field. Only the former is an error.
        const looksLikeObject = raw !== null
          && typeof raw === 'object'
          && !Array.isArray(raw)
          || (typeof raw === 'string' && (() => {
            try { const parsed = JSON.parse(raw); return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed); }
            catch { return false; }
          })());
        if (!looksLikeObject) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['agents', a, 'tools', 'custom', c, 'config'],
            message: `custom tool "${tool?.name ?? '<unnamed>'}": config must be an object`,
          });
        }
        continue;
      }

      if (containsKanbanPlaceholders(params)) continue;

      const result = validateToolInputSchema(params);
      if (!result.ok) {
        for (const err of result.errors) {
          const tail = err.path.split('.').filter(Boolean);
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['agents', a, 'tools', 'custom', c, 'config', 'parameters', ...tail],
            message: `custom tool "${tool?.name ?? '<unnamed>'}": ${err.message}${err.suggestion ? ` — ${err.suggestion}` : ''}`,
          });
        }
      }
    }
  }
}

/**
 * Walk `hub.kanban_statuses` and re-path each `kanbanStatusesArraySchema`
 * issue under `['hub', 'kanban_statuses', ...]` so the CLI surfaces field-level
 * errors (e.g. "config.hub.kanban_statuses[2].triggersAgentResponse: …").
 *
 * The schema enforces cross-field invariants (mutually-exclusive flags, exactly
 * one initial status, unique names). See contracts/setup/kanban.ts.
 */
function refineHubAsCodeKanbanStatuses(
  config: unknown,
  ctx: z.RefinementCtx,
): void {
  const hub = (config as { hub?: { kanban_statuses?: unknown } }).hub;
  if (hub == null || !('kanban_statuses' in hub)) return;
  const statuses = hub.kanban_statuses;
  if (!Array.isArray(statuses)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['hub', 'kanban_statuses'],
      message:
        statuses === null
          ? 'kanban_statuses must be an array; use `[]` to clear or omit the key to leave it unchanged'
          : 'kanban_statuses must be an array',
    });
    return;
  }

  const result = kanbanStatusesArraySchema.safeParse(statuses);
  if (!result.success) {
    for (const issue of result.error.issues) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['hub', 'kanban_statuses', ...issue.path],
        message: issue.message,
      });
    }
  }
}

/**
 * Validate `hub.lanes` shape and the lane↔status cross-reference. Re-paths issues
 * under `['hub', 'lanes', ...]` (shape) or `['hub', 'kanban_statuses', ...]`
 * (cross-reference), mirroring `refineHubAsCodeKanbanStatuses`. Lanes are a hub
 * config field; on push the full hub config is present, so both arrays are in scope.
 */
function refineHubAsCodeLanes(config: unknown, ctx: z.RefinementCtx): void {
  const hub = (config as { hub?: { lanes?: unknown; kanban_statuses?: unknown } }).hub;
  if (hub == null) return;

  if ('lanes' in hub && hub.lanes != null) {
    if (!Array.isArray(hub.lanes)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['hub', 'lanes'], message: 'lanes must be an array' });
      return;
    }
    const result = lanesArraySchema.safeParse(hub.lanes);
    if (!result.success) {
      for (const issue of result.error.issues) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['hub', 'lanes', ...issue.path], message: issue.message });
      }
      return;
    }
  }

  // Cross-reference: lane_slug must point at a declared lane; initial status has none.
  const statuses = hub.kanban_statuses;
  if (Array.isArray(statuses)) {
    for (const issue of validateLaneReferences(hub.lanes as never, statuses as never)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['hub', 'kanban_statuses', ...issue.path], message: issue.message });
    }
  }
}

/**
 * Validate the hub's localized end-user presentation (`hub.display_names`,
 * `hub.welcome_messages`, `hub.starter_questions`) with the schemas the hub update body uses:
 * supported locales only, and each entry within its bounds. The `hub` object is `.passthrough()`
 * and apply writes these maps as they come, so this refine is their validation on push and diff.
 * Absent and `null` are both valid (leave unchanged, clear).
 */
function refineHubAsCodePresentation(config: unknown, ctx: z.RefinementCtx): void {
  const hub = (config as { hub?: Record<string, unknown> }).hub;
  if (hub == null) return;
  for (const field of HUB_PRESENTATION_FIELDS) {
    const value = hub[field];
    if (value == null) continue;
    const result = HUB_PRESENTATION_SCHEMAS[field].safeParse(value);
    if (!result.success) {
      for (const issue of result.error.issues) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['hub', field, ...issue.path], message: issue.message });
      }
    }
  }
}

/**
 * Reject an eval `input.role` the fire path cannot deliver (gh #3371) — the
 * hub-as-code half of the rule the REST eval bodies enforce via the shared
 * `evalInputRoleError`. Push and diff both run this, so `wayai diff` can't
 * preview a push the apply stage would reject.
 *
 * `history` roles stay unconstrained: a tool result there answers a preceding
 * assistant `tool_call`.
 */
function refineHubAsCodeEvals(config: unknown, ctx: z.RefinementCtx): void {
  const evals = (config as { evals?: unknown }).evals;
  if (!Array.isArray(evals)) return;

  for (let e = 0; e < evals.length; e++) {
    const entry = evals[e] as { name?: string; input?: unknown } | null | undefined;
    // Same shared rule the REST bodies use (gh #4546): no new blank-named evals.
    const nameError = evalNameError(entry?.name);
    if (nameError) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['evals', e, 'name'],
        message: `evals[${e}]: ${nameError}`,
      });
    }
    const error = evalInputRoleError(entry?.input);
    if (error) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['evals', e, 'input', 'role'],
        // Named, not just indexed — the YAML author has files, not an array.
        message: `eval "${entry?.name ?? '<unnamed>'}": ${error}`,
      });
    }
    // Same shared helper the REST bodies use (gh #3430), so a malformed seed is
    // rejected at push, not silently dropped at run time.
    const initialStateError = evalInitialStateError(entry);
    if (initialStateError) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['evals', e, 'initial_state'],
        message: `eval "${entry?.name ?? '<unnamed>'}": ${initialStateError}`,
      });
    }
  }
}

/**
 * The persons a scenario's runs, or a journey's steps, are for (#5473 2-eval; a journey's by owner
 * decision 141): the shared `evalPersonIssue` the REST eval bodies use, on each entry, the retired alias
 * `customer_contact_id` refused with it (#5473 3c): apply reads an absent `person` as none and clears the stored
 * persons, so a file still naming the alias fails here rather than lose them.
 */
function refineHubAsCodePersons(config: unknown, ctx: z.RefinementCtx): void {
  for (const [key, kind] of [['evals', 'eval'], ['journeys', 'journey']] as const) {
    const entries = (config as Record<string, unknown>)[key];
    if (!Array.isArray(entries)) continue;
    entries.forEach((entry, i) => {
      const issue = evalPersonIssue(entry);
      if (issue) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [key, i, issue.key],
          message: `${kind} "${(entry as { name?: string } | null)?.name ?? '<unnamed>'}": ${issue.message}`,
        });
      }
    });
  }
}

/**
 * The hub-as-code half of the attachment rule the REST eval bodies enforce (gh #3365).
 * For every eval/journey turn: shape, per-turn cap, and USER-TURN-ONLY, via the shared
 * `evalTurnAttachmentsError` so the rejection text can't drift from REST. Then the
 * push-payload cross-check: every referenced hash must have bytes in
 * `eval_attachment_files`, and no byte carrier is orphaned — a mismatch here is an
 * author/CLI bug that would otherwise surface as a dangling ref at fire time.
 *
 * Runs on both push and diff (they share this schema), so `wayai diff` can't preview a
 * push the apply stage would reject.
 */
function refineHubAsCodeEvalAttachments(config: unknown, ctx: z.RefinementCtx): void {
  const cfg = config as {
    evals?: unknown;
    journeys?: unknown;
    eval_attachment_files?: unknown;
  };

  const referencedHashes = new Set<string>();
  const addTurnIssue = (path: (string | number)[], turn: unknown, name: string) => {
    const error = evalTurnAttachmentsError(turn);
    if (error) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path, message: `${name}: ${error}` });
      return;
    }
    for (const hash of collectTurnAttachmentHashes(turn)) referencedHashes.add(hash);
  };

  if (Array.isArray(cfg.evals)) {
    cfg.evals.forEach((entry, e) => {
      const ev = entry as { name?: string; input?: unknown; history?: unknown };
      const name = `eval "${ev?.name ?? '<unnamed>'}"`;
      addTurnIssue(['evals', e, 'input'], ev?.input, name);
      if (Array.isArray(ev?.history)) {
        ev.history.forEach((turn, h) => addTurnIssue(['evals', e, 'history', h], turn, name));
      }
    });
  }
  if (Array.isArray(cfg.journeys)) {
    cfg.journeys.forEach((entry, j) => {
      const jr = entry as { name?: string; transcript?: unknown };
      const name = `journey "${jr?.name ?? '<unnamed>'}"`;
      if (Array.isArray(jr?.transcript)) {
        jr.transcript.forEach((turn, t) => addTurnIssue(['journeys', j, 'transcript', t], turn, name));
      }
    });
  }

  const carriedHashes = new Set<string>();
  const files = cfg.eval_attachment_files;
  if (Array.isArray(files)) {
    if (files.length > MAX_EVAL_ATTACHMENT_CARRIERS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['eval_attachment_files'],
        message: `too many attachment carriers (${files.length}); the maximum is ${MAX_EVAL_ATTACHMENT_CARRIERS}`,
      });
    }
    let aggregateEncoded = 0;
    files.forEach((f, i) => {
      const h = (f as { hash?: unknown })?.hash;
      if (typeof h === 'string') carriedHashes.add(h);
      // Bound the ENCODED length so the server decode can't allocate an unbounded buffer
      // before the decoded-size check (defense in depth is the decoded check in apply).
      const b64 = (f as { content_base64?: unknown })?.content_base64;
      if (typeof b64 === 'string') {
        aggregateEncoded += b64.length;
        if (b64.length > MAX_EVAL_ATTACHMENT_ENCODED_BYTES) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['eval_attachment_files', i, 'content_base64'],
            message: `attachment ${typeof h === 'string' ? `${h.slice(0, 12)}… ` : ''}exceeds the ${(MAX_RESOURCE_FILE_SIZE / 1024 / 1024).toFixed(0)}MB size limit`,
          });
        }
      }
    });
    if (aggregateEncoded > MAX_EVAL_ATTACHMENT_AGGREGATE_ENCODED_BYTES) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['eval_attachment_files'],
        message: `total attachment bytes exceed the ${(MAX_EVAL_ATTACHMENT_AGGREGATE_ENCODED_BYTES / 1024 / 1024).toFixed(0)}MB per-push limit — split the push or reduce attachments`,
      });
    }
  }
  for (const hash of referencedHashes) {
    if (!carriedHashes.has(hash)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['eval_attachment_files'],
        message: `attachment ${hash.slice(0, 12)}… is referenced by a turn but its bytes are missing from eval_attachment_files`,
      });
    }
  }
}

/**
 * Each agent's `access_values`, when present and not null, is an access assignment's shape
 * (`heldAccessAssignmentSchema`: person access, `$person`, beside the dimensions), never with
 * `$person` on an agent whose `role` is customer-facing (`AGENT_PERSON_ACCESS_REFUSAL`, which the
 * apply holds the stored role to as well); which dimensions and values the org holds is the apply's
 * to check, against the hub's org. Runs on both push and diff (they share this schema).
 */
function refineHubAsCodeAccessValues(config: unknown, ctx: z.RefinementCtx): void {
  const agents = (config as { agents?: unknown })?.agents;
  if (!Array.isArray(agents)) return;
  agents.forEach((entry, a) => {
    const agent = entry as { name?: unknown; role?: unknown; access_values?: unknown } | null;
    if (agent?.access_values === undefined || agent.access_values === null) return;
    const name = typeof agent.name === 'string' ? agent.name : '<unnamed>';
    const parsed = heldAccessAssignmentSchema.safeParse(agent.access_values);
    if (parsed.success) {
      if (isCustomerFacingAgentRole(agent.role) && holdsPersonAccess(parsed.data)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['agents', a, 'access_values', PERSON_ACCESS_KEY],
          message: `agent "${name}": ${AGENT_PERSON_ACCESS_REFUSAL}`,
        });
      }
      return;
    }
    const issue = parsed.error.issues[0];
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['agents', a, 'access_values', ...(issue?.path ?? [])],
      message: `agent "${name}": access_values ${issue?.message ?? 'is invalid'}`,
    });
  });
}

/**
 * The hub-as-code half of a voice agent's call openings (gh #4418): each agent's
 * `call_openings`, when present, is `null` or an object keyed by the call languages, only on a
 * `pilot_voice` agent, each entry a content hash and a caption the REST route would take; and
 * the byte carriers are bounded before the server decodes them. Whether the hub holds an
 * opening that no carrier brings is the apply's to judge (`ensureCallOpeningObjects`).
 *
 * Runs on both push and diff (they share this schema).
 */
function refineHubAsCodeCallOpenings(config: unknown, ctx: z.RefinementCtx): void {
  const cfg = config as { agents?: unknown; call_opening_files?: unknown };
  if (Array.isArray(cfg.agents)) {
    cfg.agents.forEach((entry, a) => {
      const agent = entry as { name?: unknown; role?: unknown; call_openings?: unknown } | null;
      const openings = agent?.call_openings;
      if (openings === undefined || openings === null) return;
      const path = ['agents', a, 'call_openings'];
      const name = `agent "${typeof agent?.name === 'string' ? agent.name : '<unnamed>'}"`;
      if (typeof openings !== 'object' || Array.isArray(openings)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path, message: `${name}: call_openings must map a language to an opening` });
        return;
      }
      if (agent?.role !== 'pilot_voice' && Object.keys(openings).length > 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path, message: `${name}: only a pilot_voice agent has call_openings` });
      }
      // The REST route's own schemas, so a push takes exactly what the editor would.
      for (const [language, raw] of Object.entries(openings as Record<string, unknown>)) {
        const at = [...path, language];
        if (!callOpeningLanguage.safeParse(language).success) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: at, message: `${name}: call_openings language must be one of ${CALL_OPENING_LANGUAGES.join(', ')}` });
          continue;
        }
        const opening = raw as { hash?: unknown; caption?: unknown } | null;
        if (!callOpeningHash.safeParse(opening?.hash).success) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...at, 'hash'], message: `${name}: call_openings.${language} names no audio file` });
        }
        if (!callOpeningCaption.safeParse(opening?.caption).success) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...at, 'caption'],
            message: `${name}: call_openings.${language}.caption must be 1–${CALL_OPENING_CAPTION_MAX_CHARS} characters`,
          });
        }
      }
    });
  }

  const files = cfg.call_opening_files;
  if (!Array.isArray(files)) return;
  if (files.length > MAX_CALL_OPENING_CARRIERS) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['call_opening_files'],
      message: `too many call opening files (${files.length}); the maximum is ${MAX_CALL_OPENING_CARRIERS}`,
    });
  }
  let aggregateEncoded = 0;
  files.forEach((f, i) => {
    const b64 = (f as { content_base64?: unknown })?.content_base64;
    if (typeof b64 !== 'string') return;
    aggregateEncoded += b64.length;
    if (b64.length > MAX_CALL_OPENING_ENCODED_BYTES) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['call_opening_files', i, 'content_base64'],
        message: 'a call opening file exceeds the opening size limit',
      });
    }
  });
  if (aggregateEncoded > MAX_CALL_OPENING_AGGREGATE_ENCODED_BYTES) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['call_opening_files'],
      message: `total call opening bytes exceed the ${MAX_CALL_OPENING_AGGREGATE_ENCODED_BYTES / 1024 / 1024}MB per-push limit`,
    });
  }
}

/** Count UTF-8 bytes without allocating a second buffer for an already-large string. */
function utf8ByteLength(value: string): number {
  let bytes = 0;
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff && i + 1 < value.length
      && value.charCodeAt(i + 1) >= 0xdc00 && value.charCodeAt(i + 1) <= 0xdfff) {
      bytes += 4;
      i++;
    } else bytes += 3;
  }
  return bytes;
}

/**
 * Bound the resource carrier before apply decodes base64. The structural Zod
 * schemas reject malformed resources; this cross-resource pass caps total work
 * and bytes, which per-file validation alone cannot do (gh #3401). It also runs
 * when `boundedHubAsCodeResourcesSchema` refused ahead of those schemas, on
 * resources they never read, so a file here may be any value.
 */
function refineHubAsCodeResources(config: unknown, ctx: z.RefinementCtx): void {
  const resources = (config as { resources?: unknown })?.resources;
  if (!Array.isArray(resources)) return;

  let aggregateEncoded = 0;
  resources.forEach((resource, resourceIndex) => {
    const files = (resource as { files?: unknown })?.files;
    if (!Array.isArray(files)) return;
    files.forEach((file, fileIndex) => {
      const entry = (file ?? {}) as { path?: unknown; content?: unknown; content_base64?: unknown };
      if (typeof entry.content_base64 === 'string') {
        aggregateEncoded += entry.content_base64.length;
        if (entry.content_base64.length > MAX_RESOURCE_ENCODED_BYTES) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['resources', resourceIndex, 'files', fileIndex, 'content_base64'],
            message: `skill file ${typeof entry.path === 'string' ? `"${entry.path}" ` : ''}exceeds the ${(MAX_RESOURCE_FILE_SIZE / 1024 / 1024).toFixed(0)}MB size limit`,
          });
        }
      }
      if (typeof entry.content === 'string') {
        const contentBytes = utf8ByteLength(entry.content);
        aggregateEncoded += contentBytes;
        if (contentBytes > MAX_RESOURCE_FILE_SIZE) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['resources', resourceIndex, 'files', fileIndex, 'content'],
            message: `skill file ${typeof entry.path === 'string' ? `"${entry.path}" ` : ''}exceeds the ${(MAX_RESOURCE_FILE_SIZE / 1024 / 1024).toFixed(0)}MB size limit`,
          });
        }
      }
    });
  });

  if (aggregateEncoded > MAX_RESOURCE_AGGREGATE_ENCODED_BYTES) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['resources'],
      message: `total skill file bytes exceed the ${(MAX_RESOURCE_AGGREGATE_ENCODED_BYTES / 1024 / 1024).toFixed(0)}MB per-push limit`,
    });
  }
}

/**
 * Enum-validate `tools.delegation[].context_boundary` on the CI WRITE path
 * (consultant-agents PR 7 — H1.5b). `hubAsCodeConfigSchema` is `.passthrough()`, so
 * without this an arbitrary YAML string would persist straight into the tool row's
 * `execution_config.context_boundary` and the spawn handler would silently fall back
 * to the default — i.e. the admin's data-boundary choice would read as one thing in
 * the file and behave as another. Every write path validates what it persists —
 * for this field THIS refine is the validation, mirroring the REST path, which
 * enum-validates it via `addNativeToolBody`.
 *
 * Only meaningful for `type: 'hub'` delegations; a `context_boundary` on an
 * agent/team delegation is a config mistake, so it is rejected there too rather than
 * silently ignored.
 */
function refineHubAsCodeDelegation(config: unknown, ctx: z.RefinementCtx): void {
  const agents = (config as { agents?: unknown })?.agents;
  if (!Array.isArray(agents)) return;

  agents.forEach((agent, agentIndex) => {
    const delegation = (agent as { tools?: { delegation?: unknown } })?.tools?.delegation;
    if (!Array.isArray(delegation)) return;

    delegation.forEach((entry, delIndex) => {
      const del = entry as { type?: unknown; tool?: unknown; context_boundary?: unknown; allow_consultant_chain?: unknown };

      // `allow_consultant_chain` (H1.7b) is a SECURITY opt-in — it is the only thing
      // that lets a consultant start another consult. Same reasoning as the boundary
      // below: `.passthrough()` would otherwise persist an arbitrary YAML value, and
      // although the runtime narrows with `=== true` (so a bad value fails CLOSED),
      // silently ignoring `allow_consultant_chain: "yes"` would leave the file saying
      // one thing and the platform doing another. Reject at the write path instead.
      if (del.allow_consultant_chain !== undefined) {
        const chainPath = ['agents', agentIndex, 'tools', 'delegation', delIndex, 'allow_consultant_chain'];
        if (typeof del.allow_consultant_chain !== 'boolean') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: chainPath,
            message: 'allow_consultant_chain must be a boolean',
          });
        } else if (del.tool !== 'start_consult_thread') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: chainPath,
            message: `allow_consultant_chain applies only to \`tool: start_consult_thread\` (got "${String(del.tool)}")`,
          });
        }
      }

      if (del.context_boundary === undefined) return;

      const path = ['agents', agentIndex, 'tools', 'delegation', delIndex, 'context_boundary'];
      if (del.type !== 'hub') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path,
          message: `context_boundary applies only to \`type: hub\` delegations (got type "${String(del.type)}")`,
        });
        return;
      }
      if (!(CONTEXT_BOUNDARIES as readonly string[]).includes(del.context_boundary as string)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path,
          message: `context_boundary must be one of: ${CONTEXT_BOUNDARIES.join(', ')}`,
        });
      }
    });
  });
}

type HubAsCodeFlagEntry = { name?: unknown; tool?: unknown; enabled?: unknown; expose_schema_to_llm?: unknown; resources?: unknown; tools?: Record<string, unknown> };

/** A list in the config is unvalidated JSON here, its entries any value. */
const flagEntriesOf = (list: unknown): HubAsCodeFlagEntry[] => (Array.isArray(list) ? list.map((entry) => entry ?? {}) : []);

/** A name is unvalidated JSON too, and one that cannot print would throw a 500. YAML reads `name: 2024` as a number. */
const flagEntryName = (name: unknown) => (typeof name === 'string' || typeof name === 'number' ? name : '<unnamed>');

/**
 * `.passthrough()` would admit anything in a flag a file turns something off with, and `enabled: no` is a
 * string in YAML: read as anything but off, it leaves the thing on while the file says it is off. The route
 * answers with the first issue's message alone, so that message names the entity and the value. A value that
 * is not a primitive prints as its kind, since JSON.stringify overflows the stack on an array nested as deep
 * as JSON.parse takes.
 */
function refuseNonBooleanFlag(ctx: z.RefinementCtx, subject: string, entry: HubAsCodeFlagEntry, key: 'enabled' | 'expose_schema_to_llm', path: Array<string | number>): void {
  const value = entry[key];
  if (value === undefined || typeof value === 'boolean') return;
  const shown = value === null || typeof value !== 'object' ? JSON.stringify(value).slice(0, 40) : Array.isArray(value) ? 'a list' : 'an object';
  ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, key], message: `${subject}: ${key} must be true or false, got ${shown}` });
}

/**
 * The `enabled` of an agent, its skill links and tool entries, a connection, an eval and a journey, and a
 * custom tool's `expose_schema_to_llm`. A skill's `enabled` is refused ahead of its schema, by
 * `boundedHubAsCodeResourcesSchema`. A pull writes only `false` on any of them.
 */
function refineHubAsCodeFlags(config: unknown, ctx: z.RefinementCtx): void {
  const { agents, connections, evals, journeys } = config as Record<string, unknown>;

  flagEntriesOf(agents).forEach((agent, agentIndex) => {
    const subject = `Agent "${flagEntryName(agent.name)}"`;
    const at = ['agents', agentIndex];
    refuseNonBooleanFlag(ctx, subject, agent, 'enabled', at);
    flagEntriesOf(agent.resources).forEach((link, linkIndex) => {
      refuseNonBooleanFlag(ctx, `${subject}, skill "${flagEntryName(link.name)}"`, link, 'enabled', [...at, 'resources', linkIndex]);
    });
    for (const group of HUB_AS_CODE_AGENT_TOOL_KEYS) {
      flagEntriesOf(agent.tools?.[group]).forEach((entry, entryIndex) => {
        // A delegation entry names its tool under `tool`, every other under `name`.
        const tool = `${subject}, tool "${flagEntryName(entry.name ?? entry.tool)}"`;
        const entryAt = [...at, 'tools', group, entryIndex];
        refuseNonBooleanFlag(ctx, tool, entry, 'enabled', entryAt);
        if (group === 'custom') refuseNonBooleanFlag(ctx, tool, entry, 'expose_schema_to_llm', entryAt);
      });
    }
  });

  for (const [section, noun, list] of [['connections', 'Connection', connections], ['evals', 'Eval', evals], ['journeys', 'Journey', journeys]] as const) {
    flagEntriesOf(list).forEach((entry, index) => {
      refuseNonBooleanFlag(ctx, `${noun} "${flagEntryName(entry.name)}"`, entry, 'enabled', [section, index]);
    });
  }
}

/**
 * Validate flag-condition operators on the CI WRITE path — the evaluator agent's
 * top-level `flag_conditions` and the monitor's `monitor_config.flag_conditions`.
 *
 * `hubAsCodeConfigSchema` is `.passthrough()` and the apply path stores both arrays
 * as-is, so without this refine `wayai push` persisted any operator string; `pull`
 * round-tripped it and `evaluateFlagConditions` honoured none of it, leaving a
 * condition that reads as configured and never fires. Same shape of failure — and
 * the same remedy — as `refineHubAsCodeDelegation` above: the write path validates
 * what it persists, and for these fields THIS refine is the validation, mirroring
 * the REST path's `flagConditionsField` / `monitorConfigField`.
 */
function refineHubAsCodeFlagConditions(config: unknown, ctx: z.RefinementCtx): void {
  const agents = (config as { agents?: unknown })?.agents;
  if (!Array.isArray(agents)) return;

  // Deliberately the OPERATOR only, not the rest of the row: this path has never
  // carried Zod, so tightening `variable` or `value` here would 400 a push that
  // succeeds today over something this PR is not fixing. The operator is the one
  // field whose acceptance here and rejection in the evaluator produces the silent
  // never-fires condition.
  const checkList = (list: unknown, basePath: (string | number)[]): void => {
    if (!Array.isArray(list)) return;
    list.forEach((row, rowIndex) => {
      const operator = (row as { operator?: unknown } | null)?.operator;
      if (!(FLAG_CONDITION_OPERATORS as readonly unknown[]).includes(operator)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basePath, rowIndex, 'operator'],
          message: `operator must be one of: ${FLAG_CONDITION_OPERATORS.join(', ')}`,
        });
      }
    });
  };

  agents.forEach((agent, agentIndex) => {
    const a = agent as { flag_conditions?: unknown; monitor_config?: unknown };
    checkList(a?.flag_conditions, ['agents', agentIndex, 'flag_conditions']);
    const monitorConfig = a?.monitor_config;
    if (monitorConfig && typeof monitorConfig === 'object' && !Array.isArray(monitorConfig)) {
      const base = ['agents', agentIndex, 'monitor_config'];
      checkList(
        (monitorConfig as { flag_conditions?: unknown }).flag_conditions,
        [...base, 'flag_conditions'],
      );
      // THE THIRD PLACE A `FlagCondition` PERSISTS. A rule's `when` is compared by
      // the same evaluator as the two lists above, so an operator this refine did
      // not visit could push a rule whose condition the evaluator ignores — a rule
      // that reads as configured and never matches, which is the failure this
      // function exists for. The REST path reaches it through `monitorRuleSchema`.
      const rules = (monitorConfig as { rules?: unknown }).rules;
      if (Array.isArray(rules)) {
        rules.forEach((rule, ruleIndex) => {
          if (!rule || typeof rule !== 'object' || Array.isArray(rule)) return;
          checkList((rule as { when?: unknown }).when, [...base, 'rules', ruleIndex, 'when']);
        });
      }
    }
  });
}

/**
 * Validate `monitor_config.trigger` on the CI WRITE path, and the delay the
 * `idle` trigger needs.
 *
 * The same two rules `monitorConfigField` enforces for REST, restated here
 * because agents ride `hubAsCodeConfigSchema`'s `.passthrough()` and reach the
 * column with no object schema of their own — the shape that let three of the
 * four `flag_conditions` write paths accept operators the evaluator ignored.
 *
 * Why an unrecognised trigger has to be a 400 rather than a default: readers
 * resolve an unknown value to `idle` (`resolveMonitorTrigger`, which cannot fail
 * because the column is untyped at run time). A push that stored
 * `trigger: user_mesage` would therefore report success and schedule the monitor
 * on idle, with nothing anywhere to say the word was not understood.
 */
function refineHubAsCodeMonitorConfig(config: unknown, ctx: z.RefinementCtx): void {
  const agents = (config as { agents?: unknown })?.agents;
  if (!Array.isArray(agents)) return;

  agents.forEach((agent, agentIndex) => {
    const monitorConfig = (agent as { monitor_config?: unknown })?.monitor_config;
    if (!monitorConfig || typeof monitorConfig !== 'object' || Array.isArray(monitorConfig)) return;
    const basePath = ['agents', agentIndex, 'monitor_config'];

    /** Report each declared key whose value its own schema rejects. */
    const checkKeyValues = (
      keys: readonly string[],
      schemas: Record<string, z.ZodTypeAny>,
    ): void => {
      for (const key of keys) {
        const value = (monitorConfig as Record<string, unknown>)[key];
        if (value === undefined) continue;
        const parsed = schemas[key].safeParse(value);
        if (!parsed.success) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basePath, key],
            message: parsed.error?.issues[0]?.message ?? `invalid ${key}`,
          });
        }
      }
    };

    const trigger = (monitorConfig as { trigger?: unknown }).trigger;
    if (trigger !== undefined && !(MONITOR_TRIGGERS as readonly unknown[]).includes(trigger)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basePath, 'trigger'],
        message: `trigger must be one of: ${MONITOR_TRIGGERS.join(', ')}`,
      });
      // The delay rule below is keyed on the RESOLVED trigger, which reads an
      // unrecognised value as `idle`. Reporting both issues would tell the author
      // to add a delay for a trigger the same response has just rejected.
      return;
    }

    if (monitorConfigNeedsDelay(monitorConfig)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basePath, 'delay_seconds'],
        message: MONITOR_IDLE_DELAY_REQUIRED_MESSAGE,
      });
    }

    // The two input-shaping keys mean nothing on `idle`, which runs as a full turn
    // through the ordinary loader. Refused rather than ignored — the same reading
    // `monitorConfigField` applies on the REST path.
    for (const key of monitorInputShapingKeysOnIdle(monitorConfig)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basePath, key],
        message: MONITOR_INPUT_SHAPING_IDLE_MESSAGE,
      });
    }
    // `speaker` means something only to a live call's `call_utterance` monitor — the same
    // refusal `monitorConfigField` applies on the REST path.
    for (const key of monitorCallKeysOffTrigger(monitorConfig)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...basePath, key], message: MONITOR_CALL_KEY_MESSAGE });
    }

    // The VALUE checks, off the SAME schema objects the REST field spreads — so a
    // key added to either constant is gated on both write surfaces or on neither,
    // never on one alone.
    checkKeyValues(MONITOR_INPUT_SHAPING_KEYS, MONITOR_INPUT_SHAPING_SCHEMAS);
    checkKeyValues(MONITOR_RULE_KEYS, MONITOR_RULE_SCHEMAS);
    checkKeyValues(MONITOR_CALL_KEYS, MONITOR_CALL_SCHEMAS);

    // The config-only refusals both surfaces take from `collectMonitorRuleIssues`.
    // Whether the monitor CARRIES the tool a rule names is not decidable here —
    // the push's resulting set answers it, in `computeDiff`.
    for (const issue of collectMonitorRuleIssues(monitorConfig)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basePath, ...issue.path],
        message: issue.message,
      });
    }
  });
}

/**
 * Refuse a pushed agent that binds a decisions model to an output shape a
 * decisions model cannot produce.
 *
 * A pushed agent declaration carries its own role, `settings` and
 * `response_format` together, so an agent whose `settings.model` names a
 * decisions model carries everything the check needs, on create and update alike.
 * An agent declaring no model, or one the predicate does not match, passes
 * untouched — and a push that leaves `settings` off keeps whatever model is
 * stored, which only `updateAgent` in HubDO can see and where the same check also
 * runs.
 *
 * The apply path maps `response_format` presence to the column
 * (`agentConfig.response_format ? 'json_schema' : 'text'`), so the check is fed
 * the column value that push would write rather than the YAML shape.
 */
function refineHubAsCodeDecisionsModel(config: unknown, ctx: z.RefinementCtx): void {
  const agents = (config as { agents?: unknown })?.agents;
  if (!Array.isArray(agents)) return;

  agents.forEach((agent, agentIndex) => {
    const a = agent as {
      role?: unknown;
      settings?: unknown;
      response_format?: { schema_json?: unknown } | null;
    } | null;
    if (!a) return;

    const responseFormat = a.response_format;
    const refusal = checkDecisionsModelBinding({
      model: readAgentSettingsModel(a.settings),
      agent_role: a.role,
      response_format: pushedResponseFormatColumn(responseFormat),
      schema_json: responseFormat?.schema_json ?? null,
    });
    if (refusal) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['agents', agentIndex, 'settings', 'model'],
        message: refusal.message,
      });
    }
  });
}

/**
 * The `response_format` column a pushed declaration writes: apply maps the YAML key's
 * presence to `json_schema` and its absence to `text` (`apply-changes.ts`), so a refine
 * judging the column judges this value rather than the YAML shape.
 */
function pushedResponseFormatColumn(responseFormat: unknown): 'json_schema' | 'text' {
  return responseFormat ? 'json_schema' : 'text';
}

/**
 * Refuse a pushed monitor that carries rules on a trigger that reads them and declares no
 * `response_format` — the monitor that answers in prose, so no rule can match: a reply
 * gate delivers every reply unjudged, and a `user_message` or `manual` monitor never runs
 * a rule's action. The same function, and sentence, as the REST write path.
 *
 * Judged from the declaration alone, like every refine here: a push is the whole config,
 * so a monitor stored before the rule is refused until its YAML is fixed. A declaration
 * that leaves `monitor_config` off keeps the stored config, which this cannot see: the
 * push's diff judges that case against the stored row (`findMonitorRulesOutputIssues`).
 */
function refineHubAsCodeMonitorRulesOutput(config: unknown, ctx: z.RefinementCtx): void {
  const agents = (config as { agents?: unknown })?.agents;
  if (!Array.isArray(agents)) return;

  agents.forEach((agent, agentIndex) => {
    const a = agent as { role?: unknown; response_format?: unknown; monitor_config?: unknown } | null;
    if (!a) return;
    const refusal = monitorRulesStructuredOutputRefusal({
      agent_role: a.role,
      response_format: pushedResponseFormatColumn(a.response_format),
      monitor_config: a.monitor_config,
    });
    if (refusal) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['agents', agentIndex, 'response_format'], message: refusal });
    }
  });
}

/** Reject oversized collections before Zod clones and validates every nested entry. */
const boundedHubAsCodeResourcesSchema = z.unknown().superRefine((resources, ctx) => {
  if (!Array.isArray(resources)) return;
  if (resources.length > MAX_HUB_AS_CODE_RESOURCES) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `too many skills (${resources.length}); the maximum is ${MAX_HUB_AS_CODE_RESOURCES}`,
    });
    return;
  }

  flagEntriesOf(resources).forEach((skill, index) => {
    refuseNonBooleanFlag(ctx, `Skill "${flagEntryName(skill.name)}"`, skill, 'enabled', [index]);
  });

  let fileCount = 0;
  for (const resource of resources) {
    const files = (resource as { files?: unknown })?.files;
    if (!Array.isArray(files)) continue;
    fileCount += files.length;
    if (fileCount > MAX_HUB_AS_CODE_RESOURCE_FILES) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `too many skill files; the maximum is ${MAX_HUB_AS_CODE_RESOURCE_FILES}`,
      });
      return;
    }
  }
}).pipe(z.array(hubAsCodeResourceSchema));

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** The CLI release that first writes and sends the skills layout (wayai-pro/wayai#5142 step 5 D1b). */
export const MIN_SKILLS_LAYOUT_CLI = '0.3.234';

/** The refusal of a body's, or an agent's, `resources:`, which only a CLI older than the skills layout sends. */
function resourcesSectionRefusal(scope: 'hub' | 'org', subject: string): string {
  const pull = scope === 'org' ? 'wayai org pull' : 'wayai pull';
  return `${subject} \`resources\` is no longer accepted: skills are named under \`skills\`. `
    + `Update the CLI to \`@wayai/cli\` ${MIN_SKILLS_LAYOUT_CLI} or later (\`wayai update\`), then run \`${pull}\` once to convert the folder.`;
}

/**
 * Hub and org as code name their skills `skills:` (wayai-pro/wayai#5142 step 5), and an agent its
 * skill links `skills:`, each naming its skill by `skill_id`. A body carries them as the
 * `resources` (each a skill) and links (`resource_id`) the diff and apply engines read, so the
 * section meets the resource bounds and engine. The old `resources:` spelling, of a body or an
 * agent, is refused (step 5 E): only a CLI older than the skills layout sends it, and such a CLI's
 * folder may no longer match the hub. Run before validation.
 */
function skillSectionsAsResources(scope: 'hub' | 'org') {
  return (config: unknown, ctx: z.RefinementCtx): unknown => {
    if (!isPlainObject(config)) return config;
    let out = config;
    if ('resources' in out) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['resources'], message: resourcesSectionRefusal(scope, 'A push body\'s') });
      const { resources: _old, ...rest } = out;
      out = rest;
    }
    if ('skills' in out) {
      const { skills, ...rest } = out;
      out = { ...rest, resources: skills };
    }
    if (Array.isArray(out.agents)) {
      out = {
        ...out,
        agents: out.agents.map((agent, agentIndex) => {
          if (!isPlainObject(agent)) return agent;
          let next = agent;
          if ('resources' in next) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['agents', agentIndex, 'resources'],
              message: resourcesSectionRefusal(scope, 'An agent\'s'),
            });
            const { resources: _old, ...rest } = next;
            next = rest;
          }
          if (!('skills' in next)) return next;
          const { skills, ...rest } = next;
          return {
            ...rest,
            resources: Array.isArray(skills)
              ? skills.map((link) => {
                if (!isPlainObject(link)) return link;
                const { skill_id: skillId, ...linkRest } = link;
                return skillId === undefined ? linkRest : { ...linkRest, resource_id: skillId };
              })
              : skills,
          };
        }),
      };
    }
    return out;
  };
}

/**
 * Lightweight schema for HubAsCode config — validates structural requirements
 * (version, hub.name) + draft-2020-12 check on every custom tool's config
 * parameters (gh #1542) + kanban_statuses cross-field invariants + the hub's
 * localized presentation (wayai#4948) + eval names (gh #4546) and trigger roles
 * (gh #3371) + attachment refs/bytes (gh #3365) + flag-condition operators + a flag that is not a
 * boolean (gh #5670, gh #5681, gh #5691). A `skills:`
 * section, and an agent's `skills:` links, read as `resources`; `resources:` itself is refused
 * (`skillSectionsAsResources`).
 *
 * The refines are combined in a single `.superRefine` because Zod's
 * `.superRefine` returns `ZodEffects`, which can't be re-chained without
 * `.pipe()`.
 */
const hubAsCodeConfigSchema = z.preprocess(skillSectionsAsResources('hub'), z.object({
  version: z.literal(1),
  hub: z.object({
    name: z.string().min(1, 'config.hub.name is required'),
    // Apply writes it as it comes, so this is its validation on push and diff.
    end_user_daily_message_limit: endUserDailyMessageLimitField,
  }).passthrough(),
  states: z.array(hubAsCodeStateSchema).optional(),
  resources: boundedHubAsCodeResourcesSchema.optional(),
  pages: hubAsCodePagesSchema.optional(),
}).passthrough().superRefine((config, ctx) => {
  refineHubAsCodeCustomTools(config, ctx);
  refineHubAsCodeKanbanStatuses(config, ctx);
  refineHubAsCodeLanes(config, ctx);
  refineHubAsCodePresentation(config, ctx);
  refineHubAsCodeEvals(config, ctx);
  refineHubAsCodePersons(config, ctx);
  refineHubAsCodeEvalAttachments(config, ctx);
  refineHubAsCodeCallOpenings(config, ctx);
  refineHubAsCodeAccessValues(config, ctx);
  refineHubAsCodeResources(config, ctx);
  refineHubAsCodeDelegation(config, ctx);
  refineHubAsCodeFlags(config, ctx);
  refineHubAsCodeFlagConditions(config, ctx);
  refineHubAsCodeMonitorConfig(config, ctx);
  refineHubAsCodeDecisionsModel(config, ctx);
  refineHubAsCodeMonitorRulesOutput(config, ctx);
}));

// --- Param schemas ---

export const ciPullHubIdParam = z.object({
  hub_id: ciUuidSchema,
});

export const ciBranchParam = z.object({
  branch: z.string().min(1),
});

// --- Query schemas ---

export const ciPullQuery = z.object({
  organization_id: ciUuidSchema.optional(),
});

export const ciHubsQuery = z.object({
  organization_id: ciUuidSchema,
});

export const ciLookupQuery = z.object({
  path: z.string().min(1, 'path query parameter is required'),
  organization_id: ciUuidSchema.optional(),
});

export const ciBranchesQuery = z.object({
  hub_id: ciUuidSchema,
});

// --- Body schemas ---

export const ciSyncBody = z.object({
  hub_id: ciUuidSchema,
  branch_name: z.string().min(1, 'branch_name is required'),
  config: hubAsCodeConfigSchema,
  commit_sha: z.string().optional(),
});

export const ciPushBody = z.object({
  hub_id: ciUuidSchema,
  config: hubAsCodeConfigSchema,
  organization_id: ciUuidSchema.optional(),
});

export const ciDiffBody = z.object({
  hub_id: ciUuidSchema,
  config: hubAsCodeConfigSchema,
  direction: z.enum(['push', 'pull']).default('push'),
  organization_id: ciUuidSchema.optional(),
});

export const ciPublishBody = z.object({
  hub_id: ciUuidSchema,
  branch_name: z.string().min(1, 'branch_name is required'),
});

export const ciPublishPreviewBody = z.object({
  hub_id: ciUuidSchema,
});

/**
 * POST /api/ci/sync-mcp — re-discover an MCP connection's tool/resource catalog.
 * `connection` is the display name (as written in `connections[].name`) or the
 * connection UUID; the route resolves either against the hub.
 *
 * `check: true` is the read-only drift check (`wayai sync-mcp --check`): contact
 * the server and compare against the cached catalog WITHOUT writing anything —
 * so CI can detect a stale catalog (a renamed/retyped tool param the normal
 * `push`/`diff`/`sync-mcp` write path leaves invisible) without mutating state.
 */
export const ciSyncMcpBody = z.object({
  hub_id: ciUuidSchema,
  connection: z.string().min(1, 'connection is required'),
  organization_id: ciUuidSchema.optional(),
  check: z.boolean().optional(),
});

// --- Org-as-code (org-resources) schemas ---

export const ciOrgResourcesParam = z.object({
  org_id: ciUuidSchema,
});

/**
 * Lightweight passthrough schema for the org-resources payload — mirrors
 * `hubAsCodeConfigSchema`'s approach (validate structural requirements only;
 * the apply path does the authoritative shape work). Requires `version` and,
 * when present, `skills` (read as `resources`; `resources:` itself is refused:
 * `skillSectionsAsResources`) to be an array.
 */
const orgResourcesConfigSchema = z.preprocess(skillSectionsAsResources('org'), z.object({
  version: z.literal(1),
  resources: boundedHubAsCodeResourcesSchema.optional(),
}).passthrough().superRefine((config, ctx) => {
  refineHubAsCodeResources(config, ctx);
}));

export const ciOrgResourcesPushBody = z.object({
  organization_id: ciUuidSchema,
  config: orgResourcesConfigSchema,
});

export const ciOrgResourcesDiffBody = z.object({
  organization_id: ciUuidSchema,
  config: orgResourcesConfigSchema,
  direction: z.enum(['push', 'pull']).default('push'),
});

// =============================================
// Response types
// =============================================

export interface CiDiffResponse {
  has_changes: boolean;
  direction: 'push' | 'pull';
  summary: string;
  diff: unknown;
  /**
   * The hub's current server-owned `preview_label`. Surfaced so the CLI can warn
   * when a hand-edited `hub.yaml` label differs from the server — the diff engine
   * ignores `preview_label` (it's set via `wayai relabel`/replicate, not push), so
   * without this the silent no-op is invisible. Optional for back-compat with
   * older backends. See gh hub-branching.
   */
  preview_label?: string | null;
  /**
   * Non-fatal skip reasons surfaced by the diff engine (e.g. native/delegation
   * tools dropped because their connector has no connection on the hub).
   * Populated independently of `has_changes` so the CLI can display them even
   * when every change was silently dropped. Always present (possibly empty),
   * symmetric with `DiffResult.warnings`. See gh #1538.
   */
  warnings: string[];
}

export interface CiLookupResponse {
  hub_id: string;
  hub_name: string;
  hub_environment: string;
  preview_label?: string | null;
  branch_name?: string | null;
}

export interface CiOrganizationsResponse {
  organizations: Array<{ id: string; name: string }>;
}

export interface CiCreateOrganizationResponse {
  organization: {
    id: string;
    name: string;
    /** Resolved DO placement region (auto-detected from the request edge, or the
     *  caller's explicit `primary_region`). Surfaced so the CLI can show it. */
    region: string | null;
    /** True when the caller passed an explicit region; false when auto-detected
     *  (closest Cloudflare PoP). Lets the CLI phrase "closest to you" vs "as requested". */
    region_explicit: boolean;
  };
}

export interface CiHubListItem {
  hub_id: string;
  hub_name: string;
  hub_environment: string;
  preview_label: string | null;
  branch_name: string | null;
  production_hub_id: string | null;
}

export interface CiHubListResponse {
  hubs: CiHubListItem[];
}

export const CI_APPLY_ERROR_CLASSIFICATIONS = ['expected', 'internal'] as const;
export const CI_APPLY_ENTITY_KINDS = [
  'agent',
  'agent_parameter',
  'agent_resource',
  'automation',
  'connection',
  'contact_list',
  'eval',
  'hub_config',
  'journey',
  'page',
  'resource',
  'resource_file',
  'state',
  'team',
  'tool',
] as const;
export const CI_APPLY_OPERATIONS = [
  'create',
  'delete',
  'ensure_default',
  'rerender',
  'resolve',
  'sync',
  'update',
  'upload',
  'validate',
] as const;

export type CiApplyErrorClassification = typeof CI_APPLY_ERROR_CLASSIFICATIONS[number];

/**
 * A partial Hub-as-Code apply failure.
 *
 * `expected` means the submitted configuration was rejected and the caller can
 * correct it. `internal` means a platform write/runtime operation failed and
 * must be surfaced through backend error observability.
 */
const ciApplyErrorBaseSchema = z.object({
  entity_kind: z.enum(CI_APPLY_ENTITY_KINDS),
  entity_id: z.string().optional(),
  entity_name: z.string().optional(),
  operation: z.enum(CI_APPLY_OPERATIONS),
  message: z.string(),
});

export const ciApplyErrorSchema = z.discriminatedUnion('classification', [
  ciApplyErrorBaseSchema.extend({ classification: z.literal('expected') }),
  ciApplyErrorBaseSchema.extend({ classification: z.literal('internal') }),
]);

export type CiApplyError = z.infer<typeof ciApplyErrorSchema>;
export type CiExpectedApplyError = Extract<CiApplyError, { classification: 'expected' }>;
export type CiInternalApplyError = Extract<CiApplyError, { classification: 'internal' }>;

const ciSyncCountSchema = z.number().int().nonnegative();

export const ciSyncChangesSchema = z.object({
  hub_updated: z.boolean(),
  connections_created: ciSyncCountSchema,
  connections_updated: ciSyncCountSchema,
  connections_deleted: ciSyncCountSchema,
  agents_created: ciSyncCountSchema,
  agents_updated: ciSyncCountSchema,
  agents_deleted: ciSyncCountSchema,
  tools_created: ciSyncCountSchema,
  tools_updated: ciSyncCountSchema,
  tools_deleted: ciSyncCountSchema,
  states_created: ciSyncCountSchema,
  states_updated: ciSyncCountSchema,
  states_deleted: ciSyncCountSchema,
  teams_created: ciSyncCountSchema,
  teams_updated: ciSyncCountSchema,
  teams_deleted: ciSyncCountSchema,
  resources_created: ciSyncCountSchema,
  resources_updated: ciSyncCountSchema,
  resources_deleted: ciSyncCountSchema,
  resource_files_uploaded: ciSyncCountSchema,
  resource_files_deleted: ciSyncCountSchema,
  resource_links_created: ciSyncCountSchema,
  resource_links_updated: ciSyncCountSchema,
  resource_links_deleted: ciSyncCountSchema,
  evals_created: ciSyncCountSchema,
  evals_updated: ciSyncCountSchema,
  evals_deleted: ciSyncCountSchema,
  journeys_created: ciSyncCountSchema,
  journeys_updated: ciSyncCountSchema,
  journeys_deleted: ciSyncCountSchema,
  automations_created: ciSyncCountSchema,
  automations_updated: ciSyncCountSchema,
  automations_deleted: ciSyncCountSchema,
  contact_lists_created: ciSyncCountSchema,
  contact_lists_updated: ciSyncCountSchema,
  contact_lists_deleted: ciSyncCountSchema,
  // Hub pages (wayai-pro/wayai#5333): absent from a server older than its pages section.
  pages_created: ciSyncCountSchema.optional(),
  pages_updated: ciSyncCountSchema.optional(),
  pages_deleted: ciSyncCountSchema.optional(),
});

export const ciSyncWarningSchema = z.object({
  code: z.string(),
  slug: z.string(),
  name: z.string(),
  message: z.string(),
});

/**
 * Complete response contract shared by backend routes and CLI clients.
 * `errors` remains a deprecated compatibility mirror for CLI versions released
 * before structured apply errors; `apply_errors` is authoritative when present.
 */
export const ciSyncResponseSchema = z.object({
  preview_hub_id: z.string().min(1),
  created: z.boolean(),
  changes: ciSyncChangesSchema,
  errors: z.array(z.string()).optional(),
  apply_errors: z.array(ciApplyErrorSchema).optional(),
  warnings: z.array(ciSyncWarningSchema).optional(),
});

export type CiSyncResponse = z.infer<typeof ciSyncResponseSchema>;

export interface CiPublishResponse {
  synced: boolean;
  production_hub_id: string;
  /** The sync's warnings (`SyncHubResponse`); absent when there are none. */
  warnings?: SyncWarning[];
}

/**
 * One drifted tool: the cached catalog no longer matches the live MCP server.
 * `kind` — `added` (server advertises a tool not catalogued), `changed` (schema
 * or description differs — the silent, biting case), `removed` (catalogued tool
 * gone from the server). `detail` summarizes a `changed` diff, e.g.
 * "params +plan_name_search -plan_name_contains; description changed".
 */
export interface CiMcpDriftEntry {
  tool_name: string;
  kind: 'added' | 'changed' | 'removed';
  detail?: string;
}

export interface CiSyncMcpResponse {
  success: boolean;
  connection_id: string;
  connection_name: string;
  tools_count?: number;
  resources_count?: number;
  message?: string;
  warnings?: string[];
  // Drift-check mode (`check: true`) only. `drift` is present (possibly empty)
  // when the catalog was compared to the live server and NOTHING was written;
  // an empty array means "verified in sync". `reachable: false` means the server
  // or its credentials couldn't be contacted, so drift could NOT be verified —
  // distinct from "verified, no drift".
  drift?: CiMcpDriftEntry[];
  reachable?: boolean;
}

export interface CiPublishPreviewResponse {
  synced: boolean;
  production_hub_id?: string;
  /** The sync's warnings (`SyncHubResponse`); absent when there are none. */
  warnings?: SyncWarning[];
}

export interface CiBranchInfo {
  branch_name: string;
  preview_hub_id: string;
  last_ci_activity_at: string | null;
  ci_commit_sha: string | null;
}

export interface CiBranchListResponse {
  branches: CiBranchInfo[];
}

export interface CiBranchDeleteResponse {
  deleted: boolean;
  message?: string;
}

/** Pull returns the org-resources payload directly (CLI writes it to `org/`). */
export type CiOrgResourcesPullResponse = OrgResourcesPayload;

export interface CiOrgResourcesPushResponse {
  changes: {
    resources_created: number;
    resources_updated: number;
    resources_deleted: number;
    resource_files_uploaded: number;
    resource_files_deleted: number;
    resource_folders_created: number;
    resource_folders_deleted: number;
  };
  /** Aggregate awaited fan-out to linked hub shadows across all changed resources. */
  fanout: { hubs: number; failures: number };
  errors?: string[];
}
